import React, { useEffect, useState, useRef } from "react";
import { NavLink, Redirect } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import { useSipPhone } from "../../../webphone/useSipPhone";
import he from "he";
import moment from "moment-timezone";
import LogRocket from "logrocket";

import ring from "../../../assets/audio/ring.mp3";
import warning_sound_audio from "../../../assets/audio/warning.mp3";
import end_sound_audio from "../../../assets/audio/end.mp3";
import beep_sound_audio from "../../../assets/audio/beep.mp3";

import Icon from "../../../components/main/icon";
import Textarea from "../../../components/main/textarea";
import Input from "../../../components/main/input";
import Select from "../../../components/main/select";
import Button from "../../../components/main/button";
import Loader from "../../../components/main/loader";

import Misc from "../../../utils/misc";
import fetch from "../../../utils/fetch";

import { useUserDataStore } from "../../../store/userData";
import { useModalDataStore } from "../../../store/modalData";
import { useUpdateDataStore } from "../../../store/updateData";
import { useSocketDataStore } from "../../../store/socketData";
import { useActiveClientIdStore } from "../../../store/activeClientId";
import { useActiveClientStore } from "../../../store/activeClient";
import { useClientsDataStore } from "../../../store/clientsData";
import { useShowErrorFlashStore } from "../../../store/showErrorFlash";
import { useShowWarningFlashStore } from "../../../store/showWarningFlash";
import { useStatusStore } from "../../../store/statusData";
import { useSipDataStore } from "../../../store/sipData";

LogRocket.init("twr8jc/vca");

interface Answer {
    id: number;
    text: string;
    next_questions?: Question[];
}

interface Question {
    id: number;
    type: "input" | "textarea" | "select" | "checkbox" | "radio";
    text: string;
    section?: string;
    information?: string;
    answers?: Answer[];
    informationStyle?: any;
    sectionStyle?: any;
    next_questions?: Question[];
}

const _he_rounds = (text: string, rounds: number = 20) => {
    if (text === "") return text;
    let i = 0;
    while (i < rounds) {
        text = he.decode(text || "");
        i++;
    }

    return text;
};

const ConditionalForm: React.FC<any> = ({ form, update_answers, update_full_answers, update_current_question_contacts, clear = false }) => {
    const [answers, setAnswers] = useState<any>([]);
    const [currentQuestions, setCurrentQuestions] = useState<any>([]);
    const [fullQuestionAnswers, setFullQuestionAnswers] = useState<any>([]);

    const handleAnswer = (questionIndex: number, answerValue: string, question: Question, nextQuestions?: Question[], multi: any = {}) => {
        // Update answers array based on the current question index
        let updatedAnswers;
        if (Object.keys(multi).length > 0) {
            let new_value: any = JSON.stringify([]);

            if (multi?.checked === true) {
                new_value = JSON.parse(answers?.[questionIndex] || "[]");
                new_value.push(answerValue);
            } else {
                if (multi?.checked === true) {
                    new_value = JSON.parse(answers?.[questionIndex] || "[]");
                    new_value.push(answerValue);
                } else if (JSON.parse(answers?.[questionIndex] || "[]").length > 0 && multi?.checked === false) {
                    new_value = JSON.parse(new_value);
                    for (let i = 0; i < JSON.parse(answers?.[questionIndex] || "[]").length; i++) {
                        if (JSON.parse(answers?.[questionIndex] || "[]")[i] !== answerValue) new_value.push(JSON.parse(answers?.[questionIndex] || "[]")[i]);
                    }
                }
            }

            updatedAnswers = [...answers.slice(0, questionIndex), JSON.stringify(new_value)];
        } else if (["input", "textarea"].includes(question.type)) {
            // since input/textarea questions don't affect branching logic
            updatedAnswers = [
                ...answers.slice(0, questionIndex),
                answerValue,
                ...answers.slice(questionIndex + 1), // ← preserve everything after
            ];
        } else {
            updatedAnswers = [...answers.slice(0, questionIndex), answerValue];
        }

        setAnswers(updatedAnswers);

        // For input and textarea types, check if there are next questions in the first answer
        let nxtQuestions = nextQuestions;
        if (["input", "textarea"].includes(question.type) && !nxtQuestions && question.answers && question.answers.length > 0) {
            nxtQuestions = question.answers[0].next_questions;
        }

        // Check for next questions and log the relevant data
        if (nxtQuestions && nxtQuestions.length > 0) {
            setCurrentQuestions([...currentQuestions.slice(0, questionIndex + 1), ...nxtQuestions]);
        } else if (["select", "radio", "checkbox"].includes(question.type)) {
            // For select/radio/checkbox with no next questions, remove any previously added sub-questions
            // Find the next top-level question index
            const nextTopLevelIndex = currentQuestions.findIndex((q: any, idx: any) => idx > questionIndex && form?.questions?.some((topQ: any) => topQ.id === q.id));

            if (nextTopLevelIndex > -1) {
                // Keep up to current question, remove sub-questions, keep remaining top-level questions
                setCurrentQuestions([...currentQuestions.slice(0, questionIndex + 1), ...currentQuestions.slice(nextTopLevelIndex)]);
            } else {
                // No more top-level questions, just remove sub-questions
                setCurrentQuestions(currentQuestions.slice(0, questionIndex + 1));
            }
        }

        /* if (nxtQuestions) {
            console.log("Next Questions:", nxtQuestions);
            // Set the current questions to include the selected question and next questions
            setCurrentQuestions([...currentQuestions.slice(0, questionIndex + 1), ...nxtQuestions]);
        } else {
            // Log the current questions up to the selected question
            console.log("No Next Questions, keeping current:", currentQuestions.slice(0, questionIndex + 1));
            // If no next questions, keep the current questions up to the selected one
            setCurrentQuestions(currentQuestions.slice(0, questionIndex + 1));
        } */
    };

    const renderQuestion = (question: Question, qIndex: number) => {
        let extra = "";
        if (question?.section && _he_rounds(question?.section).replace(/<p>|<\/p>/g, "") !== "undefined" && _he_rounds(question?.section) !== "<p><br></p>") extra += `<div>${_he_rounds(question?.section || "")}</div>`;
        if (question?.information && _he_rounds(question?.information).replace(/<p>|<\/p>/g, "") !== "undefined" && _he_rounds(question?.information) !== "<p><br></p>") extra += `<div>${_he_rounds(question?.information || "-")}</div>`;

        switch (question?.type) {
            case "select":
                return (
                    <div key={question.id} className="pt-4 pb-4" style={{ borderBottom: "2px solid #000" }}>
                        <div dangerouslySetInnerHTML={{ __html: extra }}></div>
                        <p dangerouslySetInnerHTML={{ __html: _he_rounds(question?.text || "") }}></p>
                        <div className="input-small">
                            <select
                                onChange={(e) => {
                                    const selectedAnswer = question.answers ? question.answers[e.target.selectedIndex - 1] : undefined;
                                    if (selectedAnswer) {
                                        handleAnswer(qIndex, selectedAnswer.text, question, selectedAnswer.next_questions);
                                    }
                                }}>
                                <option value="">Select an option</option>
                                {(question?.answers || []).map((answer) => (
                                    <option key={answer.id} value={answer.text}>
                                        {answer.text}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                );
            case "input":
                return (
                    <div key={question.id} className="pt-4 pb-4" style={{ borderBottom: "2px solid #000" }}>
                        <div dangerouslySetInnerHTML={{ __html: extra }}></div>

                        <div dangerouslySetInnerHTML={{ __html: _he_rounds(question?.text || "") }}></div>
                        <Input
                            type="text"
                            name={question.id + ""}
                            placeholder={""}
                            className="input-small"
                            container={false}
                            defaultValue={answers?.[qIndex]}
                            updateChange={(v) => handleAnswer(qIndex, v, question, question.next_questions)}
                            changeDefault={new Date().getTime()}
                        />
                    </div>
                );
            case "textarea":
                return (
                    <div key={question.id} className="pt-4 pb-4" style={{ borderBottom: "2px solid #000" }}>
                        <div dangerouslySetInnerHTML={{ __html: extra }}></div>

                        <p dangerouslySetInnerHTML={{ __html: _he_rounds(question?.text || "") }}></p>

                        <Textarea name="" placeholder={""} defaultValue={answers?.[qIndex]} updateChange={(v) => handleAnswer(qIndex, v, question, question.next_questions)} />
                    </div>
                );
            case "checkbox":
                return (
                    <div key={question.id} className="pt-4 pb-4" style={{ borderBottom: "2px solid #000" }}>
                        <div dangerouslySetInnerHTML={{ __html: extra }}></div>

                        <p dangerouslySetInnerHTML={{ __html: _he_rounds(question?.text || "") }}></p>
                        {(question?.answers || []).map((answer) => (
                            <div key={answer.id}>
                                <label>
                                    <input type="checkbox" value={answer.text} onChange={(e) => handleAnswer(qIndex, answer.text, question, answer.next_questions, { checked: e.target.checked })} />
                                    <span className="ml-2" style={{ verticalAlign: "middle" }}>
                                        {answer.text}
                                    </span>
                                </label>
                            </div>
                        ))}
                    </div>
                );
            case "radio":
                return (
                    <div key={question.id} className="pt-4 pb-4" style={{ borderBottom: "2px solid #000" }}>
                        <div dangerouslySetInnerHTML={{ __html: extra }}></div>

                        <p dangerouslySetInnerHTML={{ __html: _he_rounds(question?.text || "") }}></p>
                        {(question?.answers || []).map((answer) => (
                            <div key={answer.id}>
                                <input type="radio" name={question.text} value={answer.text} onChange={(e) => handleAnswer(qIndex, answer.text, question, answer.next_questions)} />
                                <span className="ml-2" style={{ verticalAlign: "middle" }}>
                                    {answer.text}
                                </span>
                            </div>
                        ))}
                    </div>
                );
            default:
                return null;
        }
    };

    // useEffect(() => {
    //     let fullData: any = [];

    //     (answers || []).map((answer: any, index: any) => {
    //         const questionJson = currentQuestions?.[index];
    //         if (questionJson && answer) {
    //             // Only include if both question and answer exist
    //             fullData.push({
    //                 question: questionJson, // Full question object
    //                 answer: answer,
    //             });
    //         }
    //     });

    //     setFullQuestionAnswers(fullData);
    // }, [answers, currentQuestions]);

    useEffect(() => {
        let a: any = [];
        let fullData: any = [];
        {
            (answers || []).map((answer: any, index: any) => {
                a.push({
                    question: currentQuestions?.[index]?.text,
                    answer: answer,
                });

                const questionJson = currentQuestions?.[index];
                if (questionJson && answer) {
                    fullData.push({
                        question: currentQuestions?.[index]?.text, // Question text
                        answer: answer, // User's actual response only (same as above)
                        ...questionJson, // Spread ALL other question properties (id, type, section, information, etc.)
                    });
                }
            });
        }

        update_answers(a);
        setFullQuestionAnswers(fullData);
    }, [answers]);

    useEffect(() => {
        if (form) setCurrentQuestions(form?.questions || []);
        // if (form) setCurrentQuestions([form?.questions?.[0]]);
    }, [form]);

    useEffect(() => {
        if (clear === true && JSON.stringify(answers) !== "[]") {
            setAnswers([]);
            setCurrentQuestions([form?.questions?.[0]]);
            setFullQuestionAnswers([]); // NEW: Clear the full data too
        }
    }, [clear]);

    useEffect(() => {
        if (update_full_answers) {
            update_full_answers(fullQuestionAnswers);
        }
    }, [fullQuestionAnswers]);

    useEffect(() => {
        if (update_current_question_contacts && currentQuestions.length > 0) {
            const currentQuestion = currentQuestions[currentQuestions.length - 1];
            update_current_question_contacts(currentQuestion?.contacts || []);
        }
    }, [currentQuestions]);

    if (Number(form?.status || 0) !== 1) return <></>;

    return (
        <div className="col-12">
            <hr />
            {/* <h3>Conditional Form</h3> */}
            {(currentQuestions || []).map((question: any, qIndex: number) => renderQuestion(question, qIndex))}
            {/* <div className="mt-4">
                <h3>Your Answers:</h3>
                {(answers || []).map((answer: any, index: any) => (
                    <div key={index}>
                        <span>{currentQuestions?.[index]?.text}</span>
                        <p key={index}>{answer}</p>
                    </div>
                ))}
            </div> */}
        </div>
    );
};

// ── Queue feed helpers ─────────────────────────────────────────────────────
// socketCallQueue comes from the ws-vca server, whose item shape we do not
// control. Twilio-era items carry `sid` / `conference_sid` /
// `caller_called_ts`; FreeSWITCH-era (fs_call_queue) items carry `call_sid`
// and `queued_at` (int8 epoch ms, which node-postgres serialises as a
// STRING). These helpers read either shape and never throw.

const vca_digits = (v: any): string => String(v ?? "").replace(/\D/g, "");

// Fixed numbers Twilio substitutes for withheld / unknown callers. Different
// people can share these, so they never count as "the same caller".
const VCA_SHARED_CALLER_DIGITS = ["266696687", "86282452253", "8656696687", "7378742833", "2562533"];

// Twilio call SIDs on an entry (call_sid and sid hold the same value class).
const vca_queue_call_sids = (item: any): string[] => {
    const out: string[] = [];
    for (const k of ["call_sid", "sid"]) {
        const v = item?.[k];
        if (v === undefined || v === null) continue;
        const s = String(v).trim();
        if (s !== "" && !out.includes(s)) out.push(s);
    }
    return out;
};

// Identity keys used to spot the same call listed more than once.
const vca_queue_keys = (item: any): string[] => {
    const keys = vca_queue_call_sids(item).map((s) => "sid:" + s);
    if (keys.length === 0) {
        // Only when there is no call SID at all, so a shared or placeholder
        // conference_sid can never merge two calls that do have SIDs.
        const conf = String(item?.conference_sid ?? "").trim();
        if (conf !== "") keys.push("conf:" + conf);
    }
    // One phone number cannot be waiting on the same DID twice at once, so
    // a second entry with the same real caller and DID is a duplicate or a
    // ghost of the same call.
    const caller = vca_digits(item?.caller);
    const did = vca_digits(item?.did);
    if (caller.length >= 8 && did !== "" && !VCA_SHARED_CALLER_DIGITS.includes(caller)) keys.push("cd:" + caller + "|" + did);
    return keys;
};

// Drops null entries and later duplicates; keeps the first occurrence, so
// the order (and therefore entry [0]) is unchanged.
const vca_dedupe_queue = (queue: any[]): any[] => {
    const seen = new Set<string>();
    const out: any[] = [];
    for (const item of Array.isArray(queue) ? queue : []) {
        if (!item || typeof item !== "object") continue;
        const keys = vca_queue_keys(item);
        if (keys.some((k) => seen.has(k))) continue;
        keys.forEach((k) => seen.add(k));
        out.push(item);
    }
    return out;
};

// The list behind "N in queue" and "Longest in queue": this agent's answer
// groups (same test the counter has always used), then deduplicated.
const vca_queue_for_user = (queue: any[], answerGroup: any): any[] =>
    vca_dedupe_queue((Array.isArray(queue) ? queue : []).filter((item: any) => item && typeof item === "object" && (answerGroup || []).some((userAnswer: any) => (item.answer_group || []).includes(userAnswer))));

// When the call was queued, in epoch ms, or NaN if no usable value.
const vca_queue_ts = (item: any): number => {
    for (const k of ["caller_called_ts", "queued_at", "queuedAt", "created_at"]) {
        const raw = item?.[k];
        if (raw === undefined || raw === null || raw === "") continue;
        let n = raw instanceof Date ? raw.getTime() : typeof raw === "number" ? raw : Number(String(raw).trim());
        if (!Number.isFinite(n)) n = Date.parse(String(raw));
        if (!Number.isFinite(n) || n <= 0) continue;
        // Below 1e11 cannot be epoch ms of any real call (that is 1973), so
        // it is epoch seconds.
        if (n < 1e11) n = n * 1000;
        return n;
    }
    return NaN;
};

const vca_longest_in_queue = (queue: any[]): string => {
    const ts = (queue || []).map(vca_queue_ts).filter((n) => Number.isFinite(n));
    if (ts.length === 0) return "00:00";
    const waited = Math.max(0, new Date().getTime() - Math.min(...ts));
    const s = moment.utc(waited).format("mm:ss");
    return s === "Invalid date" ? "00:00" : s;
};

// Entries the legacy ring loop may act on: real objects, minus calls this
// agent has already answered (keyed by the SIP X-Vca-Call-Sid, so this is
// empty unless the server sends that header).
const vca_ring_candidates = (queue: any[], answered: Map<string, number>): any[] => (Array.isArray(queue) ? queue : []).filter((item: any) => item && typeof item === "object" && !vca_queue_call_sids(item).some((s) => answered.has(s)));

// The entry the SIP phone is actually being offered, when the INVITE's
// X-Vca-* headers identify it; otherwise entry [0], exactly as before.
const vca_pick_queue_entry = (queue: any[], offer: any): any => {
    const first = queue[0];
    if (!offer) return first;
    const sid = String(offer.callSid || "").trim();
    if (sid !== "") {
        const bySid = queue.find((item: any) => vca_queue_call_sids(item).includes(sid) || String(item?.conference_sid ?? "").trim() === sid);
        if (bySid) return bySid;
    }
    const did = vca_digits(offer.did);
    if (did === "") return first;
    const caller = vca_digits(offer.caller);
    if (caller !== "") {
        const byCaller = queue.find((item: any) => vca_digits(item?.did) === did && vca_digits(item?.caller) === caller);
        if (byCaller) return byCaller;
    }
    // [0] already rings the right DID (so the right client): keep it.
    if (vca_digits(first?.did) === did) return first;
    return queue.find((item: any) => vca_digits(item?.did) === did) || first;
};

interface Props {}

const Home: React.FC<Props> = () => {
    const user = useUserDataStore<any>((s) => s.user);
    const clients = useClientsDataStore<any>((s) => s.clients);
    const setShowErrorFlash = useShowErrorFlashStore<any>((s) => s.setShowErrorFlash);
    const setShowWarningFlash = useShowWarningFlashStore<any>((s) => s.setShowWarningFlash);
    const modalData = useModalDataStore<any>((s) => s.modalData);
    const setModalData = useModalDataStore<any>((s) => s.setModalData);
    const socketCallQueue = useSocketDataStore<any>((s) => s.socketCallQueue);
    const sipCredentials = useSipDataStore<any>((s) => s.sipCredentials);

    // ── SIP phone ──────────────────────────────────────────────────────────
    // Replaces Twilio's Device. Registers to FreeSWITCH and stays registered
    // for the whole session, because FreeSWITCH originates TO this browser
    // when a queued call is offered — the opposite of the Twilio model,
    // where the browser dialled into a conference on the agent's click.
    //
    // The UI is unchanged: socketCallQueue still drives the ringing state,
    // the client resolution and every card. Only the call transport moved.
    const phone = useSipPhone({
        credentials: sipCredentials || { sipUser: "", sipPass: "", sipDomain: "", wssUri: "" },
        onEnded: () => {
            // Replaces Twilio's per-call a.on("disconnect"). Fires for BOTH
            // inbound and outbound, and for far-end hangups as well as our
            // own, so it's the single place call teardown is handled.
            // The offered call is over; stop preferring it in the queue loop.
            sipOfferRef.current = null;
            // A call that was actually connected always goes to wrap-up when
            // it ends. Checking `status` alone missed it whenever page code had
            // already rewritten status mid-call (e.g. to "search" after a popup
            // closed), and the agent went straight to Available instead.
            const wasLive = liveCallRef.current !== "";
            liveCallRef.current = "";
            if (statusRef.current === "outbound" || statusRef.current === "inbound" || (wasLive && statusRef.current !== "wrapping up")) {
                setStatus("wrapping up");
                statusRef.current = "wrapping up";
                setTimerWrapping({ ts: new Date().getTime(), string: "00:00" });
                setTimerCallDuration({ ts: 0, string: "00:00" });
                setTimerAgentCallDuration({ ts: 0, string: "00:00" });
                setTimerClientCallDuration({ ts: 0, string: "00:00" });
                setShowKeyPad(false);
            }
        },
        onIncoming: (ctx) => {
            // Context off the INVITE's X-Vca-* headers. The socket queue
            // effect below already populates inboundCallRef, but the headers
            // are authoritative for THIS call and arrive with it, so they
            // win where present.
            sipOfferRef.current = { callSid: ctx.callSid || "", did: ctx.did || "", caller: ctx.caller || "" };
            // The server is offering this call again (e.g. re-queued), so it
            // must not stay hidden as "already answered".
            if (ctx.callSid) answeredCallSidsRef.current.delete(String(ctx.callSid).trim());
            if (ctx.did) {
                inboundCallRef.current = {
                    ...inboundCallRef.current,
                    did: ctx.did,
                    call_sid: ctx.callSid || inboundCallRef.current?.call_sid,
                    from: ctx.caller || inboundCallRef.current?.from,
                    to: ctx.did,
                };
            }
        },
    });
    const socketCallStatus = useSocketDataStore<any>((s) => s.socketCallStatus);
    const bhvmTickets = useSocketDataStore<any>((s) => s.bhvmTickets);
    const setActiveClientId = useActiveClientIdStore<any>((s) => s.setActiveClientId);
    const activeClientId = useActiveClientIdStore<any>((s) => s.activeClientId);
    const activeClientIdRef = useRef<any>(activeClientId);
    const setActiveClient = useActiveClientStore<any>((s) => s.setActiveClient);
    const activeClient = useActiveClientStore<any>((s) => s.activeClient);
    const activeClientRef = useRef<any>(activeClient);
    const setStatus = useStatusStore<any>((s) => s.setStatus);
    const status = useStatusStore<any>((s) => s.status); // '', inbound, ringing, search, outbound, wrapping up
    const statusRef = useRef<any>(status);
    const [toggleAdminSection, setToggleAdminSection] = useState<boolean>(false);
    const [searchContacts, setSearchContacts] = useState<string>("");
    const [createCustomerAlert, setCreateCustomerAlert] = useState<string>("");
    const [sendMessage, setSendMessage] = useState<any>({});
    const [activeTransfer, setActiveTransfer] = useState<number>(-1);
    const [conditionalFormAnswers, setConditionalFormAnswers] = useState<any>([]);
    const [conditionalFormAnswersFull, setConditionalFormAnswersFull] = useState<any>([]);
    const [conditionalForm, setConditionalForm] = useState<any>(null);
    const [conditionalFormView, setConditionalFormView] = useState<any>(null);
    const [outboundCall, setOutboundCall] = useState<any>({ caller_prefix: "+61" });
    const inboundCallRef = useRef<any>({});
    const outboundCallRef = useRef<any>({ caller_prefix: "+61" });
    const [customerAlerts, setCustomerAlerts] = useState<any>([]);
    const [timerLongestInQueue, setTimerLongestInQueue] = useState<string>("00:00");
    const [timerWrapping, setTimerWrapping] = useState<any>({ ts: 0, string: "00:00" });
    const [timerCallDuration, setTimerCallDuration] = useState<any>({ ts: 0, string: "00:00" });
    const [timerAgentCallDuration, setTimerAgentCallDuration] = useState<any>({ ts: 0, string: "00:00" });
    const [timerClientCallDuration, setTimerClientCallDuration] = useState<any>({ ts: 0, string: "00:00" });
    const [showKeyPad, setShowKeyPad] = useState<any>(false);
    const [messageSent, setMessageSent] = useState<any>(false);
    const [currentQuestionContacts, setCurrentQuestionContacts] = useState<string[]>([]);
    const [endCallSeconds, setEndCallSeconds] = useState<number>(0);
    const [onABreak, setOnABreak] = useState<boolean>(false);
    // Read by the 1s queue interval, whose closure would otherwise see a
    // stale onABreak.
    const onABreakRef = useRef<boolean>(false);
    // useSipPhone's call state ("idle" | "in" | "out" | "active"), for the
    // same reason.
    const phoneCallRef = useRef<string>("idle");
    // "in" or "out" from the moment a SIP call is actually connected until it
    // ends, "" otherwise. The page's `status` is not a safe answer to "is the
    // agent on a call?": older page code rewrites it (see the modal effect),
    // so call handling reads this instead.
    const liveCallRef = useRef<string>("");
    // X-Vca-* context of the INVITE currently ringing this browser, or null.
    const sipOfferRef = useRef<any>(null);
    // Call SIDs (from X-Vca-Call-Sid) this agent answered -> when. A queue
    // entry for one of these is a leftover, not a call to ring for.
    const answeredCallSidsRef = useRef<Map<string, number>>(new Map());

    const ringing = useRef(new Audio(ring));
    const warning_sound = useRef(new Audio(warning_sound_audio));
    const end_sound = useRef(new Audio(end_sound_audio));
    const beep_sound = useRef(new Audio(beep_sound_audio));
    ringing.current.loop = true;

    const [buttonActive, setButtonActive] = useState<any>({
        save_alert: false,
        send_message: false,
        check_customer_alerts: false,
        call_action: {},
    });

    const buttonActiveRef = useRef<any>({
        save_alert: false,
        send_message: false,
        check_customer_alerts: false,
        call_action: {},
    });

    // ── FreeSWITCH conference call control ────────────────────────────────
    // Hold / transfer (add a number) / merge / drop / mute used to go to the
    // ws-vca server's /user/vca/twilio/call-action with Twilio conference and
    // call SIDs. Those SIDs no longer exist under FreeSWITCH, so every one of
    // those actions failed ("SID missing") and the participant_* fields that
    // drive the call-control buttons were never populated.
    //
    // They now go to server.js's /api/conference/* routes, which act on the
    // authenticated agent's own room (adhoc_<sipUser>). Real room membership
    // (the /ws/conference push, plus /api/conference/participants) is mapped
    // back into the SAME participant_* field names the JSX already reads.
    //
    // Member roles: is_self = this agent's browser leg. Of the other members,
    // the CALLER is the one that joined first (lowest member id: mod_conference
    // hands out ids in increasing order, and a third party is always
    // originated after the call was assembled). Anyone else is the CLIENT
    // (the transfer / added party).
    //
    // None of this writes to socketCallStatus, so it can never trip the
    // auto-hangup / wrap-up checks in the socketCallStatus effect below,
    // which read that ws-vca feed directly.
    const CONF_ACTIONS = ["make call v2", "make call outbound", "hold v2", "merge call v2", "drop participant v2", "mute v2"];
    const confWsRef = useRef<WebSocket | null>(null);
    // Last membership snapshot for the current call (null = none yet).
    const confMembersRef = useRef<any[] | null>(null);
    // callerId: member id of the original caller. dialing: a third party is
    // being dialed ({ maxIdBefore, number }). cancelAbove: a dial was
    // cancelled before it answered; a member above this id is kicked on
    // arrival. ignore: member ids already kicked, never shown again.
    const confRolesRef = useRef<any>({ callerId: "", dialing: null, dialTimer: null, cancelAbove: null, cancelTimer: null, ignore: {} });
    // participant_* values derived from the room, re-applied whenever another
    // writer (ws-vca socket merge, stale setOutboundCall spreads) clobbers them.
    const confOverlayRef = useRef<any>(null);
    const [, setConfRender] = useState<number>(0);

    // server.js base URL. Same host and form the already-working conference
    // code in Dashboard.tsx uses (CONFERENCE_API_BASE / CONFERENCE_WS_BASE),
    // which is proven against the live nginx, including the WebSocket
    // upgrade on /ws/conference. Deliberately not read from a config module
    // this file has never imported.
    const conf_api_base = (): string => "https://siptestea.ypc.solutions";

    // Same identity the SIP registration uses: server.js's
    // validateSipSession(Authorization = sipUser, xAccessToken = sipPass).
    const conf_api = async (path: string, body: any = {}): Promise<any> => {
        const base = conf_api_base();
        const creds: any = sipCredentials || {};
        if (!base || !creds.sipUser || !creds.sipPass) throw new Error("conference api unavailable");
        // Bounded: /promote can legitimately take ~16s server-side (five
        // findActiveChannel attempts plus a 15s uuid_transfer), so 20s.
        const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
        const abortTimer = ctrl ? setTimeout(() => ctrl.abort(), 20000) : null;
        let res: Response;
        try {
            res = await window.fetch(`${base}/api/conference${path}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ...body, Authorization: creds.sipUser, xAccessToken: creds.sipPass }),
                signal: ctrl ? ctrl.signal : undefined,
            });
        } finally {
            if (abortTimer) clearTimeout(abortTimer);
        }
        let json: any = {};
        try {
            json = await res.json();
        } catch (e) {
            json = {};
        }
        if (!res.ok || json?.error) throw new Error(json?.error || `conference ${path} failed (${res.status})`);
        return json;
    };

    const conf_sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

    const conf_classify = (members: any[]) => {
        const roles = confRolesRef.current;
        const list = (Array.isArray(members) ? members : []).filter((m: any) => m && !roles.ignore[String(m.id)]);
        const self = list.find((m: any) => m.is_self) || null;
        const others = list.filter((m: any) => !m.is_self).sort((a: any, b: any) => Number(a.id) - Number(b.id));
        if (!roles.callerId && self && others.length > 0) {
            const limit = roles.dialing ? roles.dialing.maxIdBefore : roles.cancelAbove !== null ? roles.cancelAbove : Infinity;
            const first = others.find((m: any) => Number(m.id) <= limit);
            if (first) roles.callerId = String(first.id);
        }
        const caller = others.find((m: any) => String(m.id) === roles.callerId) || null;
        const rest = others.filter((m: any) => String(m.id) !== roles.callerId);
        const cancelled = roles.cancelAbove !== null ? rest.filter((m: any) => Number(m.id) > roles.cancelAbove) : [];
        const live = rest.filter((m: any) => !cancelled.includes(m));
        const client = live.length > 0 ? live[live.length - 1] : null;
        return { self, caller, client, cancelled };
    };

    const conf_write_overlay = (st: string) => {
        const overlay = confOverlayRef.current;
        if (!overlay) return;
        if (st === "outbound") {
            outboundCallRef.current = { ...outboundCallRef.current, ...overlay };
            setOutboundCall((prev: any) => ({ ...prev, ...overlay }));
        } else if (st === "inbound") {
            inboundCallRef.current = { ...inboundCallRef.current, ...overlay };
        }
    };

    const conf_apply = (members: any[]) => {
        const st = statusRef.current;
        if (!["inbound", "outbound"].includes(st)) return;
        if (!Array.isArray(members)) return;
        confMembersRef.current = members;
        const roles = confRolesRef.current;
        const { self, caller, client, cancelled } = conf_classify(members);
        // Not (yet, or any longer) in the room, e.g. an outbound call that is
        // still a plain bridge, or a queue call mid-assembly. Leave the fields
        // exactly as they are.
        if (!self) return;

        if (cancelled.length > 0) {
            for (const m of cancelled) {
                roles.ignore[String(m.id)] = true;
                conf_api("/kick", { memberId: m.id }).catch(() => {});
            }
            roles.cancelAbove = null;
            if (roles.cancelTimer) clearTimeout(roles.cancelTimer);
            roles.cancelTimer = null;
        }
        if (roles.dialing && client && Number(client.id) > roles.dialing.maxIdBefore) {
            roles.dialing = null;
            if (roles.dialTimer) clearTimeout(roles.dialTimer);
            roles.dialTimer = null;
        }

        const bit = (v: boolean) => (v ? 1 : 0);
        const prevClientStatus = Number(confOverlayRef.current?.participant_client_status || 0);
        const clientStatus = client ? 1 : roles.dialing ? 2 : 0;
        confOverlayRef.current = {
            participant_agent_status: 1,
            participant_agent_muted: bit(!!self.muted),
            participant_agent_hold: bit(self.can_hear === false),
            participant_caller_status: bit(!!caller),
            participant_caller_muted: bit(!!caller && !!caller.muted),
            participant_caller_hold: bit(!!caller && caller.can_hear === false),
            participant_client_status: clientStatus,
            participant_client_muted: bit(!!client && !!client.muted),
            participant_client_hold: bit(!!client && client.can_hear === false),
        };
        conf_write_overlay(st);

        if (prevClientStatus !== 1 && clientStatus === 1) setTimerClientCallDuration({ ts: new Date().getTime(), string: "00:00" });
        if (prevClientStatus === 1 && clientStatus !== 1) setTimerClientCallDuration({ ts: 0, string: "00:00" });
        if (prevClientStatus !== 0 && clientStatus === 0) setActiveTransfer(-1);
        setConfRender((n: number) => n + 1);
    };

    const conf_refresh = async (): Promise<any[] | null> => {
        try {
            const r = await conf_api("/participants");
            const members = Array.isArray(r?.members) ? r.members : [];
            conf_apply(members);
            return members;
        } catch (e) {
            return null;
        }
    };

    // The WebSocket handlers and timers outlive a render, so they call the
    // latest versions through refs rather than a stale closure.
    const confApplyRef = useRef<any>(conf_apply);
    confApplyRef.current = conf_apply;
    const confRefreshRef = useRef<any>(conf_refresh);
    confRefreshRef.current = conf_refresh;

    const conf_has_call = (members: any[] | null) => !!members && members.some((m: any) => m && m.is_self) && members.some((m: any) => m && !m.is_self);

    // Make sure the agent's current call is a conference with this agent in
    // it. An answered inbound call already is. An outbound call is a plain
    // bridge until /promote moves both legs into adhoc_<sipUser>; promote is
    // a no-op on a call that is already a conference. Returns the members,
    // or null if the agent's own leg never showed up in the room (in which
    // case nothing may be held or dialed).
    const conf_ensure = async (): Promise<any[] | null> => {
        let members = await conf_refresh();
        if (conf_has_call(members)) return members;
        await conf_api("/promote");
        for (let i = 0; i < 12; i++) {
            members = await conf_refresh();
            if (conf_has_call(members)) return members;
            await conf_sleep(400);
        }
        return null;
    };

    // Contacts are stored in assorted formats; the gateway needs E.164. Only
    // characters that can be part of a phone number survive, which also keeps
    // the value safe inside server.js's originate command.
    const conf_normalise_number = (raw: any): string => {
        let n = String(raw || "").replace(/[\s\-().]/g, "");
        if (n.startsWith("00")) n = "+" + n.slice(2);
        else if (/^0\d{8,}$/.test(n)) n = "+61" + n.slice(1);
        else if (/^61\d{9}$/.test(n)) n = "+" + n;
        else if (/^1[38]00\d{6}$/.test(n) || /^13\d{4}$/.test(n)) n = "+61" + n;
        return /^\+?\d{6,15}$/.test(n) ? n : "";
    };

    const conf_flash_error = () => {
        setShowErrorFlash(true);
        setTimeout(() => {
            setShowErrorFlash(false);
        }, 500);
    };

    const conference_action = async (action: string, data: any = {}, actionKey: string = action) => {
        const setBusy = (v: boolean) => {
            buttonActiveRef.current = { ...buttonActiveRef.current, call_action: { ...buttonActiveRef.current.call_action, [actionKey]: v, [action]: v } };
            setButtonActive((prev: any) => ({ ...prev, call_action: { ...prev.call_action, [actionKey]: v, [action]: v } }));
        };
        setBusy(true);
        try {
            // The SIP call decides whether there is a call to act on. `status`
            // can have been rewritten by older page code mid-call, which made
            // Mute and Transfer silently refuse to run.
            const st = ["inbound", "outbound"].includes(statusRef.current) ? statusRef.current : liveCallRef.current === "out" ? "outbound" : liveCallRef.current === "in" ? "inbound" : statusRef.current;
            if (!["inbound", "outbound"].includes(st) || phone.call !== "active") throw new Error("no active call");
            const roles = confRolesRef.current;

            if (action === "make call v2" || action === "make call outbound") {
                // Transfer / add a second number: caller on hold, third party
                // dialed into the agent's conference. Agent talks to the third
                // party privately; Merge joins everyone, Return to caller
                // cancels, End call completes the transfer.
                if (st === "inbound") inboundCallRef.current = { ...inboundCallRef.current, last_call_attempt: data?.phone_number || "" };
                if (st === "outbound") {
                    outboundCallRef.current = { ...outboundCallRef.current, last_call_attempt: data?.phone_number || "" };
                    setOutboundCall((prev: any) => ({ ...prev, last_call_attempt: data?.phone_number || "" }));
                }
                const number = conf_normalise_number(data?.phone_number);
                if (!number) throw new Error("invalid phone number");
                if (roles.dialing) throw new Error("a third party is already being dialed");
                if (roles.cancelAbove !== null) throw new Error("a cancelled dial is still being cleared");
                const members = await conf_ensure();
                if (!members) throw new Error("agent is not in a conference");
                const { caller, client } = conf_classify(members);
                if (client) throw new Error("a third party is already on the call");
                if (!caller) throw new Error("caller is not in the conference");
                await conf_api("/hold", { memberId: caller.id });
                const maxIdBefore = Math.max(0, ...members.map((m: any) => Number(m?.id) || 0));
                roles.dialing = { number, maxIdBefore };
                if (roles.dialTimer) clearTimeout(roles.dialTimer);
                // server.js rings for originate_timeout=25s; past that nobody
                // is joining, so stop showing "Ringing".
                roles.dialTimer = setTimeout(() => {
                    if (confRolesRef.current === roles && roles.dialing) {
                        roles.dialing = null;
                        roles.dialTimer = null;
                        confRefreshRef.current();
                    }
                }, 30000);
                // Inbound: prefer the DID from the SIP offer itself (X-Vca-Did,
                // set by server.js on the ring). inboundCallRef.did can be
                // overwritten by the ws-vca socketCallStatus merge with an
                // empty or non-E.164 value, which server.js rightly refuses,
                // so every inbound transfer would fail.
                const did = (st === "inbound" ? phone.callContext?.did || inboundCallRef.current?.did : outboundCallRef.current?.did || outboundCall?.did) || "";
                try {
                    await conf_api("/add", did ? { number, callerId: did } : { number });
                } catch (e) {
                    roles.dialing = null;
                    if (roles.dialTimer) clearTimeout(roles.dialTimer);
                    roles.dialTimer = null;
                    await conf_api("/unhold", { memberId: caller.id }).catch(() => {});
                    throw e;
                }
                await conf_refresh();
            } else if (action === "hold v2") {
                const hold = Number(data?.hold || 0) === 1;
                const members = hold ? await conf_ensure() : await conf_refresh();
                if (!conf_has_call(members)) throw new Error("agent is not in a conference");
                const { caller, client } = conf_classify(members as any[]);
                const target = data?.participant === "client" ? client : caller;
                if (hold) {
                    if (!target) throw new Error("participant is not in the conference");
                    await conf_api("/hold", { memberId: target.id });
                } else {
                    // "Return to caller": cancel the transfer. Drop the third
                    // party (or the dial still ringing), then take the caller
                    // off hold.
                    if (data?.participant !== "client") {
                        if (client) {
                            roles.ignore[String(client.id)] = true;
                            await conf_api("/kick", { memberId: client.id });
                        }
                        if (roles.dialing) {
                            // Stop the ring on the server, so a late answer
                            // cannot join this or the agent's next call. The
                            // cancelAbove kick below stays as a backstop for
                            // an answer that races the cancel.
                            await conf_api("/add/cancel").catch(() => {});
                            roles.cancelAbove = roles.dialing.maxIdBefore;
                            roles.dialing = null;
                            if (roles.dialTimer) clearTimeout(roles.dialTimer);
                            roles.dialTimer = null;
                            if (roles.cancelTimer) clearTimeout(roles.cancelTimer);
                            roles.cancelTimer = setTimeout(() => {
                                if (confRolesRef.current === roles) roles.cancelAbove = null;
                            }, 35000);
                        }
                    }
                    if (!target) throw new Error("participant is not in the conference");
                    await conf_api("/unhold", { memberId: target.id });
                }
                await conf_refresh();
            } else if (action === "merge call v2") {
                // Everyone talks together: take caller (and third party, if
                // held) off hold. The agent stays on the call; End call then
                // leaves the caller and third party connected.
                const members = await conf_refresh();
                if (!conf_has_call(members)) throw new Error("agent is not in a conference");
                const { caller, client } = conf_classify(members as any[]);
                if (!caller || !client) throw new Error("caller and third party must both be on the call");
                await conf_api("/unhold", { memberId: caller.id });
                if (client.can_hear === false) await conf_api("/unhold", { memberId: client.id });
                await conf_refresh();
            } else if (action === "drop participant v2") {
                const members = await conf_refresh();
                if (!conf_has_call(members)) throw new Error("agent is not in a conference");
                const { caller, client } = conf_classify(members as any[]);
                const target = data?.participant === "caller" ? caller : client;
                if (target) {
                    roles.ignore[String(target.id)] = true;
                    await conf_api("/kick", { memberId: target.id });
                } else if (data?.participant !== "caller" && roles.dialing) {
                    // Still ringing: stop the ring on the server, and as a
                    // backstop kick it if it answers anyway.
                    await conf_api("/add/cancel").catch(() => {});
                    roles.cancelAbove = roles.dialing.maxIdBefore;
                    roles.dialing = null;
                    if (roles.dialTimer) clearTimeout(roles.dialTimer);
                    roles.dialTimer = null;
                    if (roles.cancelTimer) clearTimeout(roles.cancelTimer);
                    roles.cancelTimer = setTimeout(() => {
                        if (confRolesRef.current === roles) roles.cancelAbove = null;
                    }, 35000);
                } else {
                    throw new Error("participant is not in the conference");
                }
                await conf_refresh();
            } else if (action === "mute v2") {
                const members = await conf_ensure();
                if (!members) throw new Error("agent is not in a conference");
                const { self } = conf_classify(members);
                if (!self) throw new Error("agent is not in the conference");
                await conf_api(Number(data?.mute || 0) === 1 ? "/mute" : "/unmute", { memberId: self.id });
                await conf_refresh();
            }
        } catch (e: any) {
            console.warn("[CONF]", action, e?.message || e);
            if ((action === "make call v2" || action === "make call outbound") && !confRolesRef.current.dialing) setActiveTransfer(-1);
            conf_flash_error();
            conf_refresh();
        } finally {
            setBusy(false);
        }
    };

    // Completing a transfer is the agent leaving: their own SIP leg ends and
    // mod_conference keeps the caller and third party connected. If the caller
    // is still on hold at that point they would be left deaf and mute, so take
    // them off hold first. Bounded so End call is never held up for long.
    const conf_prepare_leave = async () => {
        try {
            if (!["inbound", "outbound"].includes(statusRef.current)) return;
            // Ending the call while a third party is still ringing: stop that
            // ring, or it answers into an empty room or the agent's next call.
            if (confRolesRef.current?.dialing) {
                confRolesRef.current.dialing = null;
                await Promise.race([conf_api("/add/cancel").catch(() => {}), conf_sleep(2500)]);
            }
            const members = confMembersRef.current;
            if (!conf_has_call(members)) return;
            const { caller, client } = conf_classify(members as any[]);
            if (!caller || !client) return;
            if (caller.can_hear === false || caller.muted) await Promise.race([conf_api("/unhold", { memberId: caller.id }), conf_sleep(2500)]);
        } catch (e) {
            // never block hanging up
        }
    };

    // A new call, or the end of one: forget the previous call's roles.
    useEffect(() => {
        const roles = confRolesRef.current;
        if (roles?.dialTimer) clearTimeout(roles.dialTimer);
        if (roles?.cancelTimer) clearTimeout(roles.cancelTimer);
        confRolesRef.current = { callerId: "", dialing: null, dialTimer: null, cancelAbove: null, cancelTimer: null, ignore: {} };
        confMembersRef.current = null;
        confOverlayRef.current = null;
    }, [status]);

    // Live membership while (and only while) a call is up. The WebSocket is
    // the primary feed; if it is not open, /participants is polled instead.
    useEffect(() => {
        if (!["inbound", "outbound"].includes(status)) return;
        const sipUser = sipCredentials?.sipUser;
        const sipPass = sipCredentials?.sipPass;
        const base = conf_api_base();
        if (!sipUser || !sipPass || !base) return;
        let closed = false;
        let retry: any = null;
        const connect = () => {
            if (closed) return;
            let ws: WebSocket;
            try {
                ws = new WebSocket(`${base.replace(/^http/i, "ws")}/ws/conference?Authorization=${encodeURIComponent(sipUser)}&xAccessToken=${encodeURIComponent(sipPass)}`);
            } catch (e) {
                retry = setTimeout(connect, 3000);
                return;
            }
            confWsRef.current = ws;
            ws.onmessage = (evt: any) => {
                try {
                    const msg = JSON.parse(evt.data);
                    if (msg?.type === "participants") confApplyRef.current(msg.members || []);
                } catch (e) {
                    // ignore malformed frames
                }
            };
            ws.onerror = () => {};
            ws.onclose = () => {
                if (confWsRef.current === ws) confWsRef.current = null;
                if (!closed) retry = setTimeout(connect, 3000);
            };
        };
        connect();
        const poll = setInterval(() => {
            if (confWsRef.current && confWsRef.current.readyState === 1) return;
            confRefreshRef.current();
        }, 4000);
        return () => {
            closed = true;
            if (retry) clearTimeout(retry);
            clearInterval(poll);
            const ws = confWsRef.current;
            confWsRef.current = null;
            if (ws) {
                ws.onclose = null;
                ws.onmessage = null;
                try {
                    ws.close();
                } catch (e) {
                    // already closed
                }
            }
        };
    }, [status, sipCredentials?.sipUser, sipCredentials?.sipPass]);

    // Several places spread a stale `outboundCall` closure back into state;
    // put the room-derived fields back if one of those wiped them.
    useEffect(() => {
        const overlay = confOverlayRef.current;
        if (!overlay || statusRef.current !== "outbound") return;
        if (Object.keys(overlay).some((k) => outboundCall?.[k] !== overlay[k])) {
            outboundCallRef.current = { ...outboundCallRef.current, ...overlay };
            setOutboundCall((prev: any) => ({ ...prev, ...overlay }));
        }
    }, [outboundCall]);

    let vca_left_body = (ref: any, extra: any[] = []) => {
        if (!ref) return;
        const resizeObserver = new ResizeObserver(() => {
            // @ts-ignore
            const headerHeight = (extra || []).map((id) => document.querySelector(id)?.offsetHeight || 0).reduce((a, b) => a + b, 0);
            // @ts-ignore
            ref.style.height = `${document.getElementById("vca-left")?.offsetHeight - headerHeight}px`;
        });
        // @ts-ignore
        extra.forEach((id) => resizeObserver.observe(document.querySelector(id)));
    };

    let vca_right_contact = (ref: any) => {
        if (!ref) return;
        const resizeObserver = new ResizeObserver(() => {
            // @ts-ignor-e
            const headerHeight = ["#vca-right-phone-inbound", "#vca-right-phone-outbound"]
                .map((id) => {
                    const _a: any = document.querySelector(id);
                    if (_a) return _a.offsetHeight || 0;
                })
                .reduce((a: any, b: any) => a + b, 0);
            // const headerHeight = ["#vca-right-phone-inbound", "#vca-right-phone-outbound"].map((id) => document.querySelector(id)?.offsetHeight || 0).reduce((a, b) => a + b, 0);
            // @ts-ignor-e
            const _a = document.getElementById("vca-left");
            if (_a) ref.style.height = `${_a.offsetHeight - headerHeight - 60}px`;
        });
        // @ts-ignore
        ["#vca-right-phone-inbound", "#vca-right-phone-outbound"].forEach((id) => resizeObserver.observe(document.querySelector(id)));
    };

    const send_message_state_prep = () => {
        if (modalData.type === "location search") return;
        if (activeClientId !== 0) {
            let obj: any = {};
            for (let i = 0; i < (clients || []).length; i++) {
                if (Number(clients[i].id) === activeClientId) {
                    obj = clients[i];
                    break;
                }
            }
            setActiveClient(obj);
            const _form = (obj?.forms || []).length > 0 ? obj?.forms[0].form.form.form || [] : [];
            let _custom: any = [];
            for (let i = 0; i < _form.length; i++) {
                _custom.push({
                    [_form[i].label]: {
                        type: _form[i].type,
                        value: _form[i].type === "multiplechoice" ? [] : "",
                    },
                });
            }
            let caller_number = inboundCallRef.current.from || outboundCall.to || "";
            for (let index = 0; index < (obj.dids || []).length; index++) {
                if ((obj.dids || [])[index].did === inboundCallRef.current.did && Number((obj.dids || [])[index].hide_cli) === 1) {
                    caller_number = "";
                    break;
                }
            }

            setSendMessage({
                account_id: Number(activeClientId),
                custom: _custom,
                source: "email",
                conf_sid: inboundCallRef.current.conference_sid || outboundCall.conference_sid || null,
                caller_number: caller_number,
                caller_call_sid: inboundCallRef.current.participant_caller_call_sid || outboundCall.participant_caller_call_sid || "",
            });
            setConditionalFormAnswers([]);
            setCustomerAlerts([]);

            const params = new URLSearchParams(window.location.search);
            const start_call_did = params.get("start_call_did")?.replace(/ /g, "+");
            const start_call_cli = params.get("start_call_cli")?.replace(/ /g, "+").replace("+61", "");
            const start_call_sid = params.get("start_call_sid");

            if (start_call_did && start_call_cli) {
                params.delete("start_call_did");
                params.delete("start_call_cli");
                const newUrl = window.location.pathname;
                window.history.replaceState(null, "", newUrl + "?bhvm_sid=" + start_call_sid);
                make_outbound("false", { caller_prefix: "+61", did: start_call_did ? start_call_did : outboundCall.did, destination: start_call_cli ? start_call_cli : outboundCall.destination }, true);

                //     setOutboundCall({ ...outboundCall, caller_prefix: "+61", did: start_call_did ? start_call_did : outboundCall.did, destination: start_call_cli ? start_call_cli : outboundCall.destination });
                // outboundCallRef.current = {
                //     ...outboundCall,
                //     caller_prefix: "+61",
                //     did: start_call_did ? start_call_did : outboundCallRef.current.did,
                //     destination: start_call_cli ? start_call_cli : outboundCallRef.current.destination,
                // };
            }

            const _a = document.getElementById("vca-right-contacts");
            if (_a) _a.scrollTop = 0;
            const _b = document.getElementById("vca-left-message");
            if (_b) _b.scrollTop = 0;
            const _c = document.getElementById("vca-left-booking");
            if (_c) _c.scrollTop = 0;
            const _d = document.getElementById("vca-left-info");
            if (_d) _d.scrollTop = 0;
        }
    };

    const save_alert = async () => {
        setButtonActive({ ...buttonActive, save_alert: true });

        const post = new fetch(
            "/user/vca/create/alert",
            {
                account_id: activeClientId,
                customer_uuid: sendMessage?.customer_uuid || "",
                alert: createCustomerAlert || "",
                caller_fn: sendMessage.caller_first_name || "",
                caller_ln: sendMessage.caller_last_name || "",
                caller_number: outboundCall.destination || "",
                caller_prefix: outboundCall.caller_prefix || "",
            },
            { success: true, error: true },
        ).post();

        post.then((res) => {
            if (res.status === "error" || res === false) {
                setShowErrorFlash(true);
                setTimeout(() => {
                    setShowErrorFlash(false);
                }, 500);
            }

            setButtonActive({ ...buttonActive, save_alert: false });
        }).catch((e) => {
            setButtonActive({ ...buttonActive, save_alert: false });
        });
    };

    const send_message = async () => {
        if (buttonActive.send_message) return;

        try {
            setButtonActive({ ...buttonActive, send_message: true });

            const post = new fetch(
                "/user/vca/create/messages",
                {
                    ...sendMessage,
                    conditional_form: JSON.parse(_he_rounds(JSON.stringify(conditionalFormAnswers))) || [],
                    conditional_form_full: JSON.parse(_he_rounds(JSON.stringify(conditionalFormAnswersFull))) || [],
                    caller_call_sid: inboundCallRef.current.participant_caller_call_sid || outboundCallRef.current.participant_caller_call_sid || "",
                    outbound_agent_call_sid: outboundCallRef.current.participant_agent_call_sid || "",
                },
                { success: true, error: true },
            ).post();

            post.then((res) => {
                if (res.status === "error" || res === false) {
                    setShowErrorFlash(true);
                    setTimeout(() => {
                        setShowErrorFlash(false);
                    }, 500);
                }
                if (res.status === "success") {
                    send_message_state_prep();
                    setMessageSent(true);
                }
                setButtonActive({ ...buttonActive, send_message: false });
            }).catch((e) => {
                setButtonActive({ ...buttonActive, send_message: false });
            });
        } catch (e) {
            alert("Error sending message");
            setButtonActive({ ...buttonActive, send_message: false });
        }
    };

    const check_customer_alerts = async (phone_number: string, account_id: string | number = activeClientId) => {
        setButtonActive({ ...buttonActive, check_customer_alerts: true });
        const get = new fetch(`/user/vca/search/customer?account_id=${account_id}&type=phone_number&q=${phone_number}`).get();

        get.then((res) => {
            if (res.status === "error" || res === false) {
                setShowErrorFlash(true);
                setTimeout(() => {
                    setShowErrorFlash(false);
                }, 500);
            }
            if (res.status === "success") {
                setCustomerAlerts(res.data);
                if (["ringing", "inbound"].includes(status)) {
                    inboundCallRef.current = { ...inboundCallRef.current, caller_search: res.data || [], caller_name: (res.data || []).length > 0 ? (res.data[0].first_name || "") + " " + (res.data[0].last_name || "") : "" };
                }
                if (["search", "outbound"].includes(status)) {
                    setOutboundCall({ ...outboundCall, caller_search: res.data || [], caller_name: (res.data || []).length > 0 ? (res.data[0].first_name || "") + " " + (res.data[0].last_name || "") : "" });
                    outboundCallRef.current = { ...outboundCall, caller_search: res.data || [], caller_name: (res.data || []).length > 0 ? (res.data[0].first_name || "") + " " + (res.data[0].last_name || "") : "" };
                }
            }
            setButtonActive({ ...buttonActive, check_customer_alerts: false });
        }).catch((e) => {
            setButtonActive({ ...buttonActive, check_customer_alerts: false });
        });
    };

    const force_end_call = async (sid: string) => {
        const post = new fetch(`/user/vca/twilio/call-action`, { sid: sid, action: "force end call", conference_sid: outboundCall.conference_sid }).post();

        post.then((res) => {
            if (res.status === "error" || res === false) return force_end_call(sid);
        });
        post.catch(() => {
            return force_end_call(sid);
        });
    };

    const call_status = async (action: string, state_data: any = {}) => {
        const get = new fetch(`/user/vca/twilio/call-status?conference_sid=${status === "inbound" ? inboundCallRef.current.conference_sid : outboundCall.conference_sid}`).get();

        get.then(async (res) => {
            if (res.status === "error" || res === false) {
                return await call_status(action);
            }
            if (res.status === "success") {
                if (status === "inbound") {
                    inboundCallRef.current = {
                        ...inboundCallRef.current,
                        last_call_attempt: action === "make call v2" ? state_data?.last_call_attempt : inboundCallRef.current?.last_call_attempt || "",
                        participant_agent_call_sid: res.data.participant_agent_call_sid,
                        participant_agent_hold: res.data.participant_agent_hold,
                        participant_agent_muted: res.data.participant_agent_muted,
                        participant_agent_status: res.data.participant_agent_status,
                        participant_caller_call_sid: res.data.participant_caller_call_sid,
                        participant_caller_hold: res.data.participant_caller_hold,
                        participant_caller_muted: res.data.participant_caller_muted,
                        participant_caller_status: res.data.participant_caller_status,
                        participant_client_call_sid: res.data.participant_client_call_sid,
                        participant_client_hold: res.data.participant_client_hold,
                        participant_client_muted: res.data.participant_client_muted,
                        participant_client_status: res.data.participant_client_status,
                    };
                }
                if (status === "outbound") {
                    setOutboundCall({
                        ...outboundCall,
                        last_call_attempt: action === "make call outbound" ? state_data?.last_call_attempt : outboundCall?.last_call_attempt || "",
                        participant_agent_call_sid: res.data.participant_agent_call_sid,
                        participant_agent_hold: res.data.participant_agent_hold,
                        participant_agent_muted: res.data.participant_agent_muted,
                        participant_agent_status: res.data.participant_agent_status,
                        participant_caller_call_sid: res.data.participant_caller_call_sid,
                        participant_caller_hold: res.data.participant_caller_hold,
                        participant_caller_muted: res.data.participant_caller_muted,
                        participant_caller_status: res.data.participant_caller_status,
                        participant_client_call_sid: res.data.participant_client_call_sid,
                        participant_client_hold: res.data.participant_client_hold,
                        participant_client_muted: res.data.participant_client_muted,
                        participant_client_status: res.data.participant_client_status,
                    });

                    outboundCallRef.current = {
                        ...outboundCall,
                        last_call_attempt: action === "make call outbound" ? state_data?.last_call_attempt : outboundCall?.last_call_attempt || "",
                        participant_agent_call_sid: res.data.participant_agent_call_sid,
                        participant_agent_hold: res.data.participant_agent_hold,
                        participant_agent_muted: res.data.participant_agent_muted,
                        participant_agent_status: res.data.participant_agent_status,
                        participant_caller_call_sid: res.data.participant_caller_call_sid,
                        participant_caller_hold: res.data.participant_caller_hold,
                        participant_caller_muted: res.data.participant_caller_muted,
                        participant_caller_status: res.data.participant_caller_status,
                        participant_client_call_sid: res.data.participant_client_call_sid,
                        participant_client_hold: res.data.participant_client_hold,
                        participant_client_muted: res.data.participant_client_muted,
                        participant_client_status: res.data.participant_client_status,
                    };
                }

                if (["make call v2", "make call outbound"].includes(action)) {
                    if (![0, 1].includes(Number(res.data.participant_client_status))) {
                        setTimeout(async () => {
                            await call_status(action, state_data);
                        }, 500);
                    }
                    if ([0, 1].includes(Number(res.data.participant_client_status))) {
                        setTimerClientCallDuration({ ...timerClientCallDuration, ts: Number(res.data.participant_client_status) === 0 ? 0 : new Date().getTime() });
                    }
                }
            }
        }).catch(async (e) => {
            await call_status(action, state_data);
        });
    };

    const call_action = async (action: string, data: any = {}) => {
        const actionKey = ["make call v2", "make call outbound"].includes(action) ? action + data.btn_index : action;

        // Check the ref immediately - this prevents duplicate clicks
        if (buttonActiveRef.current.call_action[actionKey] === true) return;

        // Call control runs against the FreeSWITCH conference (see
        // conference_action). Only bookkeeping actions ("wrapping up done",
        // "no caller", ...) still go to ws-vca below, unchanged.
        if (CONF_ACTIONS.includes(action)) return conference_action(action, data, actionKey);

        try {
            // Update both ref AND state
            buttonActiveRef.current = { ...buttonActiveRef.current, call_action: { ...buttonActiveRef.current.call_action, [actionKey]: true } };
            setButtonActive((prev: any) => ({ ...prev, call_action: { ...prev.call_action, [actionKey]: true } }));

            // A ws-vca request that never returns used to leave the button
            // spinning until "Clear spinning". After 15s release the button
            // (ref and state) so it can be pressed again; the request itself
            // is not cancelled and its handlers below still run if it lands.
            const watchdog = setTimeout(() => {
                if (buttonActiveRef.current.call_action[actionKey] !== true) return;
                buttonActiveRef.current = { ...buttonActiveRef.current, call_action: { ...buttonActiveRef.current.call_action, [actionKey]: false } };
                setButtonActive((prev: any) => ({ ...prev, call_action: { ...prev.call_action, [actionKey]: false } }));
            }, 15000);

            const post = new fetch(`/user/vca/twilio/call-action`, {
                ...data,
                action: action,
                conference_sid: inboundCallRef.current.conference_sid || outboundCall.conference_sid_uuid || outboundCall.conference_sid,
                caller_call_sid: inboundCallRef.current.participant_caller_call_sid || outboundCallRef.current.participant_caller_call_sid || "",
                outbound_agent_call_sid: outboundCallRef.current.participant_agent_call_sid || "",
            }).post();

            post.then((res) => {
                if (res.status === "error" || res === false) {
                    setShowErrorFlash(true);
                    setTimeout(() => {
                        setShowErrorFlash(false);
                    }, 500);
                }
                if (res.status === "success") {
                    agent_call_status();
                    if (action !== "wrapping up done") {
                        call_status(action, ["make call v2", "make call outbound"].includes(action) ? { last_call_attempt: data?.phone_number || "" } : {});
                        buttonActiveRef.current = { ...buttonActiveRef.current, call_action: { ...buttonActiveRef.current.call_action, [action]: false } };
                        setButtonActive((prev: any) => ({ ...prev, call_action: { ...prev.call_action, [action]: false } }));
                    }
                    if (action === "merge call v2") {
                        if (statusRef.current === "inbound" || status === "inbound") {
                            setStatus("wrapping up");
                            statusRef.current = "wrapping up";
                            setTimerWrapping({ ...timerWrapping, ts: new Date().getTime() });
                            setTimerCallDuration({ ts: 0, string: "00:00" });
                            setTimerAgentCallDuration({ ts: 0, string: "00:00" });
                            setTimerClientCallDuration({ ts: 0, string: "00:00" });
                            setShowKeyPad(false);
                        }
                    }
                }
                if (["wrapping up done", "no caller"].includes(action)) {
                    buttonActiveRef.current = { ...buttonActiveRef.current, call_action: { ...buttonActiveRef.current.call_action, [action]: false } };
                    setButtonActive((prev: any) => ({ ...prev, call_action: { ...prev.call_action, [action]: false } }));
                    inboundCallRef.current = {};
                    outboundCallRef.current = {};
                    setActiveClientId(0);
                    // console.log("bbbbb");
                    setActiveClient({});
                    setSendMessage({});
                    setConditionalFormAnswers([]);
                    setCustomerAlerts([]);
                    setStatus("");
                    statusRef.current = "";
                    setMessageSent({});
                    activeClientIdRef.current = 0;
                    activeClientRef.current = {};
                } else {
                    buttonActiveRef.current = { ...buttonActiveRef.current, call_action: { ...buttonActiveRef.current.call_action, [action]: false } };
                    setButtonActive((prev: any) => ({ ...prev, call_action: { ...prev.call_action, [action]: false } }));
                }
            })
                .catch((e) => {
                    buttonActiveRef.current = { ...buttonActiveRef.current, call_action: { ...buttonActiveRef.current.call_action, [actionKey]: false } };
                    setButtonActive((prev: any) => ({ ...prev, call_action: { ...prev.call_action, [actionKey]: false } }));
                    if (action === "make call v2") {
                        inboundCallRef.current = { ...inboundCallRef.current, last_call_attempt: data?.phone_number || "" };
                    }
                })
                .finally(() => {
                    clearTimeout(watchdog);
                    setTimeout(() => {
                        buttonActiveRef.current = { ...buttonActiveRef.current, call_action: { ...buttonActiveRef.current.call_action, [actionKey]: false } };
                        setButtonActive((prev: any) => ({ ...prev, call_action: { ...prev.call_action, [actionKey]: false } }));
                    }, 500);
                });
        } catch (e) {
            // console.log(e);
        }
    };

    const stop_audio = (a: any) => {
        a.current.pause();
        a.current.currentTime = 0;
    };

    const agent_call_status = async (fresh = false) => {
        const get = new fetch(`/user/vca/twilio/agent-call-status?conference_sid=${inboundCallRef.current.conference_sid || outboundCall.conference_sid_uuid || outboundCall.conference_sid || (fresh === true ? "fresh" : "")}`).get();

        get.then(async (res) => {
            if (res.status === "error" || res === false) {
                return await agent_call_status();
            }
            if (res.status === "success") {
                if (["in-progress", "wrapping-up", ""].includes(res.data.status)) {
                    setStatus(res.data.status === "wrapping-up" ? "wrapping up" : res.data.status === "in-progress" ? statusRef.current : "");
                    statusRef.current = res.data.status === "wrapping-up" ? "wrapping up" : res.data.status === "in-progress" ? statusRef.current : "";
                    if (
                        JSON.stringify(sendMessage) === "{}" &&
                        res.data.status === "wrapping-up" &&
                        ((inboundCallRef.current || outboundCall)?.participant_agent_status === 0 || JSON.stringify(inboundCallRef.current || outboundCall) === "{}" || inboundCallRef.current?.data === null) &&
                        activeClientIdRef.current === 0
                    ) {
                        outerLoop: for (let i = 0; i < (clients || []).length; i++) {
                            for (let index = 0; index < clients[i].dids.length; index++) {
                                if (clients[i].dids[index].did === res.data.did) {
                                    setActiveClientId(Number(clients[i].id));
                                    // console.log("ccccc");
                                    setActiveClient(clients[i]);
                                    activeClientIdRef.current = Number(clients[i].id);
                                    activeClientRef.current = clients[i];
                                    // check_customer_alerts(inboundCallRef.current.from, Number(clients[i].id));
                                    if (status === "inbound") {
                                        inboundCallRef.current = { ...inboundCallRef.current, did: res.data.did, conference_sid: res.data.conference_sid };
                                    } else {
                                        // Might fix message miss match errors
                                        // setOutboundCall({ ...outboundCall, did: res.data.did, conference_sid: res.data.conference_sid });
                                        // outboundCallRef.current = {
                                        //     ...outboundCall,
                                        //     did: res.data.did,
                                        //     conference_sid: res.data.conference_sid,
                                        // };
                                    }
                                    break outerLoop;
                                }
                            }
                        }
                    }
                    if (res.data.status === "wrapping-up") {
                        setTimerWrapping({ ...timerWrapping, ts: Number(res.data.ts) });
                    }
                }
            }
        }).catch(async (e) => {
            await agent_call_status();
        });
    };

    const make_outbound = async (isBillable: string, start_call: any = {}, is_bhvm: boolean = false) => {
        if (["+611300", "+611800", "+6113"].some((p) => (start_call?.did || outboundCall.did).replace(/ /g, "").trim().startsWith(p))) return alert("You cannot make a call from this number.");
        setButtonActive({ ...buttonActive, outbound: true });
        const uuid = await new fetch(`/user/vca/create/uuid`).get();
        if (uuid.status === "success") {
            if ((uuid.data.uuid || "").length === 36) {
                setStatus("outbound");
                statusRef.current = "outbound";
                // Dial straight out through FreeSWITCH. The DID travels as
                // the X-Vca-Caller-Id header for ypc_outbound.xml to apply as
                // the presented caller ID — that dialplan REQUIRES it and
                // rejects calls without a valid one, deliberately, so a
                // missing DID here fails the call rather than presenting the
                // wrong number.
                const _to = ((start_call?.caller_prefix || outboundCall.caller_prefix) + (start_call?.destination || outboundCall.destination)).replace(/ /g, "").trim();
                const _from = (start_call?.did || outboundCall.did).replace(/ /g, "").trim();
                phone.dial(_to, _from);

                setOutboundCall({
                    ...outboundCall,
                    did: (start_call?.did || outboundCall.did).replace(/ /g, "").trim(),
                    conference_sid: uuid.data.uuid,
                    conference_sid_uuid: uuid.data.uuid,
                    from: (start_call?.did || outboundCall.did).replace(/ /g, "").trim(),
                    to: _to,
                });

                outboundCallRef.current = {
                    ...outboundCall,
                    did: (start_call?.did || outboundCall.did).replace(/ /g, "").trim(),
                    conference_sid: uuid.data.uuid,
                    conference_sid_uuid: uuid.data.uuid,
                    from: (start_call?.did || outboundCall.did).replace(/ /g, "").trim(),
                    to: _to,
                };

                setTimerAgentCallDuration({ ts: new Date().getTime() });
                setTimerCallDuration({ ts: new Date().getTime() });

                // Twilio's per-call a.on("cancel") / a.on("disconnect") are
                // gone. There's one SIP session at a time, so its lifecycle
                // is handled once in useSipPhone's onEnded (see the hook
                // mount above) rather than re-registered on every dial.
            }
        } else {
            if (uuid.status === "error" || uuid === false) {
                setShowErrorFlash(true);
                setTimeout(() => {
                    setShowErrorFlash(false);
                }, 500);
            }
        }
        setButtonActive({ ...buttonActive, outbound: false });
    };

    const answer_call_v2 = async () => {
        setButtonActive({ ...buttonActive, outbound: true, send_message: false, ["wrapping up done"]: false });
        stop_audio(ringing);
        setEndCallSeconds(0);

        setStatus("inbound");
        statusRef.current = "inbound";

        // FreeSWITCH has ALREADY rung this browser — the ring engine
        // originated to us and useSipPhone is holding the inbound session.
        // So this just answers it, rather than placing an outbound call into
        // a conference the way twilioDevice.connect() did.
        phone.answer();

        setTimerAgentCallDuration({ ts: new Date().getTime() });
        setTimerCallDuration({ ts: new Date().getTime() });

        const _has_call: any = async (participant_caller_call_sid = "") => {
            const get = await new fetch(`/user/vca/twilio/call-status?version=2&participant_caller_call_sid=${participant_caller_call_sid}`).get();

            if (get?.status !== "success") return _has_call(participant_caller_call_sid);
            if (get?.status === "success") {
                if (get?.data?.participant_agent_call_sid === "") return _has_call(participant_caller_call_sid);
                // Twilio compared its own CallSid here to spot a stale
                // session. There's exactly one SIP session at a time now, so
                // there's nothing to compare — the check is kept only for the
                // "agent call sid vanished" case it also caught.
                if (!get?.data?.participant_agent_call_sid) {
                    inboundCallRef.current = {};
                    outboundCallRef.current = {};
                    setTimerCallDuration({ ts: 0, string: "00:00" });
                    setTimerAgentCallDuration({ ts: 0, string: "00:00" });
                    setTimerClientCallDuration({ ts: 0, string: "00:00" });
                    setShowKeyPad(false);
                    setActiveClientId(0);
                    // console.log("ddddd");
                    setActiveClient({});
                    activeClientIdRef.current = 0;
                    activeClientRef.current = {};
                    setSendMessage({});
                    setConditionalFormAnswers([]);
                    setCustomerAlerts([]);
                    setStatus("");
                    statusRef.current = "";
                    setMessageSent({});
                }
            }
        };

        // _has_call(a.customParameters.get("caller_sid"));

        // setOutboundCall({
        //     ...outboundCall,
        //     did: outboundCall.did,
        //     conference_sid: uuid.data.uuid,
        //     conference_sid_uuid: uuid.data.uuid,
        //     from: outboundCall.did,
        //     to: outboundCall.caller_prefix + outboundCall.destination,
        //     twilio_active_call: a,
        // });
        // setTimerAgentCallDuration({ ts: new Date().getTime() });
        // setTimerCallDuration({ ts: new Date().getTime() });

        // Twilio's a.on("cancel") / a.on("disconnect") for the inbound path
        // are gone — call teardown is handled once in useSipPhone's onEnded
        // (see the hook mount near the top), which covers both directions and
        // both local and far-end hangups.
        // }

        setButtonActive({ ...buttonActive, outbound: false });
    };

    const completed_caller_alert = async (data: any) => {
        //

        try {
            const post = new fetch(`/user/vca/update/alerts`, {
                action: "caller",
                customer_uuid: data.customer_uuid,
                alert_uuid: data.alert_uuid,
                account_id: data.account_id,
            }).post();

            post.then((res) => {
                if (res.status === "error" || res === false) {
                    setShowErrorFlash(true);
                    setTimeout(() => {
                        setShowErrorFlash(false);
                    }, 500);
                }
                if (res.status === "success") {
                    check_customer_alerts(outboundCall.caller_prefix + outboundCall.destination);
                }
            }).catch((e) => {});
        } catch (e) {
            // console.log(e);
        } finally {
        }
    };

    const is_on_break = async () => {
        try {
            const post = new fetch(`/user/vca/on-a-break`, {}).get();

            post.then((res) => {
                if (res.status === "success") {
                    setOnABreak(res.data.on_a_break);
                }
            }).catch((e) => {});
        } catch (e) {
            // console.log(e);
        } finally {
        }
    };

    const on_a_break = async (action: string) => {
        try {
            const post = new fetch(`/user/vca/on-a-break`, { action: action }).post();

            post.then((res) => {
                if (res.status === "success") {
                    if (action === "break") {
                        // Silence now, not on the next render / interval tick.
                        onABreakRef.current = true;
                        stop_audio(ringing);
                        // A queue call still ringing this browser would keep
                        // sounding until the server's next tick cancels it.
                        // Decline it now (486, as the server's own cancel
                        // does); the server re-offers it to other agents. Only
                        // while not yet answered.
                        if (["", "ringing"].includes(statusRef.current) && phoneCallRef.current === "in") {
                            try {
                                phone.reject();
                            } catch (e) {
                                // session already gone; nothing to silence
                            }
                        }
                        setOnABreak(true);
                    }
                    if (action === "back") {
                        onABreakRef.current = false;
                        setOnABreak(false);
                        // create inpoint,save action in db
                        // check status on login.
                        // blank screen
                        //
                    }
                }
            }).catch((e) => {});
        } catch (e) {
            // console.log(e);
        } finally {
        }
    };

    // DTMF goes over the SIP session (RFC 2833 / SIP INFO) rather than
    // Twilio's sendDigits. useSipPhone holds the only active session, so
    // there's no inbound/outbound branching needed here anymore.
    const handleSendDigit = (digit: string) => {
        phone.sendDTMF(digit);
    };

    useEffect(() => {
        // console.log(customerAlerts, ">>");
        const prev_status = statusRef.current;
        statusRef.current = status;
        if (statusRef.current === "") {
            setTimerCallDuration({ ts: 0, string: "00:00" });
            setTimerAgentCallDuration({ ts: 0, string: "00:00" });
            setTimerClientCallDuration({ ts: 0, string: "00:00" });
            setShowKeyPad(false);
            setActiveClientId(0);
            setActiveClient({});
            activeClientIdRef.current = 0;
            activeClient.current = {};
            setSendMessage({});
            setConditionalFormAnswers([]);
            setCustomerAlerts([]);
            setMessageSent({});
        }
        if (!["", "ringing"].includes(statusRef.current)) stop_audio(ringing);
        if (statusRef.current === "ringing") {
            if (JSON.stringify(inboundCallRef.current) !== "{}" && activeClientId === 0) {
                outerLoop: for (let i = 0; i < (clients || []).length; i++) {
                    for (let index = 0; index < (clients[i].dids || []).length; index++) {
                        if (clients[i].dids[index].did === inboundCallRef.current.did) {
                            setActiveClientId(Number(clients[i].id));
                            setActiveClient(clients[i]);
                            activeClientIdRef.current = Number(clients[i].id);
                            activeClientRef.current = clients[i];
                            check_customer_alerts(inboundCallRef.current.from, Number(clients[i].id));
                            break outerLoop;
                        }
                    }
                }
            }
        }
        if (prev_status === "ringing" && statusRef.current === "" && statusRef.current !== "wrapping up" && prev_status !== "wrapping up") {
            // call was canceled by caller
            setTimerCallDuration({ ts: 0, string: "00:00" });
            setTimerAgentCallDuration({ ts: 0, string: "00:00" });
            setTimerClientCallDuration({ ts: 0, string: "00:00" });
            setShowKeyPad(false);
            setActiveClientId(0);
            // console.log("aaaa");
            setActiveClient({});
            activeClientIdRef.current = 0;
            activeClient.current = {};
            setSendMessage({});
            setConditionalFormAnswers([]);
            setCustomerAlerts([]);
            setMessageSent({});
        }
    }, [status, inboundCallRef.current]);

    useEffect(() => {
        const _is_true = (obj: any): boolean => Object.values(obj).some((val) => (typeof val === "object" ? _is_true(val) : val === true));
        if (_is_true(buttonActive) === false) {
            if (status === "outbound") {
                setOutboundCall({
                    ...outboundCall,
                    ...socketCallStatus,
                });
                outboundCallRef.current = {
                    ...outboundCall,
                    ...socketCallStatus,
                };
            } else {
                inboundCallRef.current = {
                    ...inboundCallRef.current,
                    ...socketCallStatus,
                };
            }
            // ws-vca's Twilio-shaped participant_* values must not overwrite
            // the ones derived from the real FreeSWITCH room.
            if (confOverlayRef.current) conf_write_overlay(status);
        }
        if (status === "inbound") {
            if (Number(socketCallStatus.participant_agent_status) === 1 && Number(socketCallStatus.participant_caller_status) === 0 && Number(socketCallStatus.participant_client_status) === 0) {
                phone.hangup();
            }
        }
        if (![1, 2].includes(Number(confOverlayRef.current ? confOverlayRef.current.participant_client_status : socketCallStatus.participant_client_status))) {
            setActiveTransfer(-1);
        }
        // if ([1, 2].includes(Number(socketCallStatus.participant_client_status))) {
        //     beep_sound.current.play();
        // }
        if (Number(socketCallStatus.participant_agent_status) === 0 && socketCallStatus.status === "wrapping-up") {
            setStatus("wrapping up");
            statusRef.current = "wrapping up";
        }
    }, [socketCallStatus]);

    useEffect(() => {
        const interval = setInterval(() => {
            // Same list as the "in queue" counter: this agent's answer
            // groups, one entry per call. Reads caller_called_ts or
            // queued_at (string or number, ms or s); falls back to 00:00.
            const myQueue = vca_queue_for_user(socketCallQueue, user.answer_group);
            const a = myQueue.length === 0 ? "00:00" : vca_longest_in_queue(myQueue);
            setTimerLongestInQueue(a);

            if (!["inbound", "outbound"].includes(statusRef.current)) {
                if (["", "ringing"].includes(statusRef.current)) {
                    // remove answer screen if call no inbound and no longer active

                    if (statusRef.current === "ringing") {
                        let sids = [];
                        // for (let i = 0; i < socketCallQueue.length; i++) sids.push(socketCallQueue[i].conference_sid);

                        // if (!sids.includes(inboundCallRef.current.conference_sid)) {
                        //     setShowKeyPad(false);
                        //     setActiveClientId(0);
                        //     setActiveClient({});
                        //     setSendMessage({});
                        //     // setConditionalFormAnswers([]);
                        //     setCustomerAlerts([]);
                        //     setStatus("");
                        //     statusRef.current = "";
                        //     setMessageSent({});
                        //     activeClientIdRef.current = 0;
                        //     activeClientRef.current = {};
                        //     agent_call_status();
                        // }
                    }

                    // Null entries and calls this agent already answered are
                    // not calls to ring for.
                    const candidates = vca_ring_candidates(socketCallQueue, answeredCallSidsRef.current);
                    if (candidates.length > 0) {
                        // The call the SIP phone is being offered when the
                        // INVITE headers identify it, else entry [0] as before.
                        const entry = vca_pick_queue_entry(candidates, sipOfferRef.current);
                        let skip = true;

                        for (let index = 0; index < (user.answer_group || []).length; index++) {
                            for (let index2 = 0; index2 < (entry.answer_group || []).length; index2++) {
                                if ((user.answer_group || [])[index] === (entry.answer_group || [])[index2]) {
                                    skip = false;
                                }
                            }
                        }

                        // Only ring and clear ringing status if status available or ringing
                        if (statusRef.current === "wrapping up" && socketCallQueue.length > 0 && skip === false) {
                            ringing.current.play();
                            // if no call in queue dont ring
                        }

                        if (skip === true) return;

                        // On a break: no ring, no "ringing" status, no client
                        // load. Polled every tick, so it also stops a ring that
                        // was already sounding when Break was clicked, and
                        // resumes on the first tick after "Back".
                        if (onABreakRef.current) {
                            stop_audio(ringing);
                            return;
                        }

                        // Already on a live SIP call: a waiting call must not
                        // ring through the computer on top of it.
                        if (["active", "out"].includes(phoneCallRef.current)) stop_audio(ringing);
                        else ringing.current.play();

                        inboundCallRef.current = {
                            ...inboundCallRef.current,
                            did: entry.did,
                            conference_sid: entry.conference_sid,
                            call_sid: entry.sid,
                            from: entry.caller,
                            to: entry.did,
                        };
                        outerLoop: for (let i = 0; i < (clients || []).length; i++) {
                            for (let index = 0; index < (clients[i].dids || []).length; index++) {
                                if (clients[i].dids[index].did === entry.did) {
                                    setActiveClientId(Number(clients[i].id));
                                    setActiveClient(clients[i]);
                                    activeClientIdRef.current = Number(clients[i].id);
                                    activeClientRef.current = clients[i];
                                    check_customer_alerts(entry.caller, Number(clients[i].id));
                                    break outerLoop;
                                }
                            }
                        }
                        // console.log("zzzz");
                        if (statusRef.current !== "wrapping up") {
                            setStatus("ringing");
                            statusRef.current = "ringing";
                        }
                    } else if (statusRef.current !== "wrapping up") {
                        setStatus("");
                        statusRef.current = "";
                        stop_audio(ringing);
                    }
                }
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [socketCallQueue]);

    // Break silences the computer ring immediately (including on page load
    // when is_on_break() reports a break), and the loop above stays quiet
    // for as long as it lasts.
    useEffect(() => {
        onABreakRef.current = !!onABreak;
        if (onABreak) stop_audio(ringing);
    }, [onABreak]);

    // Once the SIP call is up, however it was answered, the computer ring
    // stops, and the answered call is remembered so a leftover queue entry
    // for it cannot start the ring again after wrap-up.
    useEffect(() => {
        phoneCallRef.current = phone.call;
        if (phone.call === "active") {
            liveCallRef.current = phone.direction === "out" ? "out" : "in";
            stop_audio(ringing);
            const sid = phone.direction === "in" ? String(sipOfferRef.current?.callSid || "").trim() : "";
            if (sid !== "") {
                const now = new Date().getTime();
                answeredCallSidsRef.current.forEach((at, key) => {
                    if (now - at > 30 * 60 * 1000) answeredCallSidsRef.current.delete(key);
                });
                answeredCallSidsRef.current.set(sid, now);
            }
        }
    }, [phone.call]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (status === "wrapping up") {
                const a = timerWrapping.ts === 0 ? "00:00" : moment.utc(new Date().getTime() - timerWrapping.ts).format("mm:ss");
                setTimerWrapping({ ts: timerWrapping.ts, string: a });
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [timerWrapping.ts]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (["inbound", "outbound"].includes(status)) {
                const a = timerAgentCallDuration.ts === 0 ? "00:00" : moment.utc(new Date().getTime() - timerAgentCallDuration.ts).format("mm:ss");
                if (status === "inbound") {
                    const _total = Number(new Date().getTime() - timerAgentCallDuration.ts) / 1000;
                    let max_call_duration = { warning: 0, end: 0 };
                    if (user.max_call_duration?.end) {
                        max_call_duration = user.max_call_duration;
                        if (Number(max_call_duration?.warning) !== 0 && _total >= Number(max_call_duration?.warning) && _total <= Number(max_call_duration?.warning) + 1) {
                            warning_sound.current.play();
                            setShowWarningFlash(true);
                            setTimeout(() => {
                                setShowWarningFlash(false);
                            }, 500);
                        }
                        if (Number(max_call_duration?.end) !== 0 && _total >= Number(max_call_duration?.end) && _total <= Number(max_call_duration?.end) + 1) {
                            end_sound.current.play();
                            setShowErrorFlash(true);
                            setTimeout(() => {
                                setShowErrorFlash(false);
                            }, 500);
                        }
                    } else {
                        max_call_duration = (activeClient?.dids || "").length > 0 ? activeClient?.dids[activeClient?.dids.findIndex((a: any) => a.did === inboundCallRef.current.did)]?.max_call_duration : { warning: 0, end: 0 };
                        if (Number(max_call_duration?.warning || 0) !== 0 && _total >= Number(max_call_duration?.warning || 0) && _total <= Number(max_call_duration?.warning || 0) + 1) {
                            warning_sound.current.play();
                            setShowWarningFlash(true);
                            setTimeout(() => {
                                setShowWarningFlash(false);
                            }, 500);
                        }
                        if (Number(max_call_duration?.end || 0) !== 0 && _total >= Number(max_call_duration?.end || 0) && _total <= Number(max_call_duration?.end || 0) + 1) {
                            end_sound.current.play();
                            setShowErrorFlash(true);
                            setTimeout(() => {
                                setShowErrorFlash(false);
                            }, 500);
                        }
                    }
                }
                setTimerAgentCallDuration({ ts: timerAgentCallDuration.ts, string: a });
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [timerAgentCallDuration.ts]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (["inbound", "outbound"].includes(status)) {
                const a = timerClientCallDuration.ts === 0 ? "00:00" : moment.utc(new Date().getTime() - timerClientCallDuration.ts).format("mm:ss");
                setTimerClientCallDuration({ ts: timerClientCallDuration.ts, string: a });
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [timerClientCallDuration.ts]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (["inbound", "outbound"].includes(status)) {
                const a = timerCallDuration.ts === 0 ? "00:00" : moment.utc(new Date().getTime() - timerCallDuration.ts).format("mm:ss");
                setTimerCallDuration({ ...timerCallDuration, ts: timerCallDuration.ts, string: a });
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [timerCallDuration.ts]);

    useEffect(() => {
        if (JSON.stringify(activeClientRef.current) !== JSON.stringify(activeClient)) activeClientRef.current = activeClient;
        if (JSON.stringify(activeClientIdRef.current) !== JSON.stringify(activeClientId)) activeClientIdRef.current = activeClientId;
    }, [activeClient, activeClientId, activeClientRef, activeClientIdRef]);

    useEffect(() => {
        activeClientIdRef.current = activeClientId;
        // Closing a popup or changing the selected client used to set status
        // to "search" (or "") even during a call. Mute, Transfer and the
        // end-of-call wrap-up all key off status, so a popup closed mid-call
        // silently broke all three, and the queue loop then loaded another
        // call's client. A live call's status is now left alone.
        const onLiveCall = liveCallRef.current !== "" || ["inbound", "outbound"].includes(statusRef.current) || ["inbound", "outbound"].includes(status);
        if (JSON.stringify(modalData) === "{}" && onLiveCall) {
            // Same message-form prep the original ran in this case (only when
            // a client is selected but not yet loaded), WITHOUT the status
            // change. send_message_state_prep resets the message form, so it
            // must not run on every popup close or it wipes what the agent typed.
            if (activeClientId !== 0 && JSON.stringify(activeClient) === "{}") send_message_state_prep();
        } else if (JSON.stringify(modalData) === "{}") {
            if (!["ringing", "wrapping up"].includes(status)) {
                setStatus(activeClientId !== 0 ? "search" : "");
                statusRef.current = activeClientId !== 0 ? "search" : "";
                if (status === "search" && activeClientId !== 0 && JSON.stringify(activeClient) === "{}") {
                    send_message_state_prep();
                }
            } else if (["ringing"].includes(status)) {
                send_message_state_prep();
            }
        } else {
            send_message_state_prep();
        }
    }, [activeClientId, modalData]);

    // Twilio's Device is gone. Registration now happens in useSipPhone
    // (mounted above), which registers to FreeSWITCH over WSS. Unlike
    // Twilio — where the browser only ever placed OUTBOUND calls into a
    // conference (device.audio.incoming(false)) — FreeSWITCH originates TO
    // the browser, so the phone must stay registered whenever an agent is
    // logged in, not just while a call is up.

    useEffect(() => {
        const handle = (e: any) => {
            if (e.metaKey && e.key === "j" && !["inbound", "wrapping up"].includes(status)) {
                setModalData({ toggle: true, type: "vca search client" });
            }
        };
        window.addEventListener("keydown", handle);
        return () => window.removeEventListener("keydown", handle);
    }, []);

    useEffect(() => {
        is_on_break();
        agent_call_status(true);
        LogRocket.identify(user.user_id, {
            name: user.first_name,
        });
    }, []);

    if (onABreak) {
        return (
            <div style={{ width: "100vw", height: "100vh", position: "absolute", top: 0, left: 0, zIndex: 99999, backgroundColor: "white" }}>
                <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", fontSize: "50px", textAlign: "center" }}>
                    You are on a break <br />{" "}
                    <Button
                        onClick={() => {
                            on_a_break("back");
                        }}
                        value="Back"
                        hideContainer
                        className="mt-5"
                    />
                </div>
            </div>
        );
    }

    return (
        <>
            {/* Audio sink for the remote party. Without this element the
                hook's audioRef.current stays null, so the inbound stream is
                never assigned anywhere and the agent hears nothing — while
                still being heard perfectly, because their own mic track is
                added by getUserMedia regardless. That is the one-way-audio
                symptom. @twilio/voice-sdk created and managed its own hidden
                audio element internally, which is why nothing like this was
                needed before the migration and why its absence was easy to
                miss. Must stay mounted for the whole session rather than only
                while a call is up: it has to exist BEFORE ontrack fires. */}
            <audio ref={phone.audioRef} autoPlay playsInline />
            <div id="vca">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-xxl-10 col-lg-9 pl-0">
                            <div id="vca-left">
                                <div id="vca-header">
                                    <div className="row">
                                        <div className="col-lg-12">
                                            <div className={Number(user.bhvm_tickets) === 1 && (bhvmTickets || []).length > 0 ? "container-fluid vca-emergency-header" : "d-none"}>
                                                <div className="row">
                                                    <div className="col-6 text-left">
                                                        <div>
                                                            <span className="bhvm-count">{(bhvmTickets || []).length}</span> pending BHVM{(bhvmTickets || []).length > 1 ? "'s" : ""}
                                                        </div>
                                                    </div>
                                                    <div className="col-6 text-right">
                                                        <Button
                                                            onClick={() => {
                                                                setModalData({ toggle: true, type: "bhvm tickets", position: "full" });
                                                            }}
                                                            value={`Manage ticket${(bhvmTickets || []).length > 1 ? "'s" : ""}`}
                                                            hideContainer
                                                            small
                                                            className="ml-4"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        {/* <div className="col-12" style={{ overflow: "hidden" }}>
                                            <div style={{ border: "1px solid red", overflow: "hidden", whiteSpace: "pre", width: "100%" }}>
                                                <small style={{ fontSize: "10px", overflow: "hidden" }}>
                                                    {statusRef?.current || "-"}
                                                    {" <> "}
                                                    {status || "-"}
                                                    {" <> "}
                                                    {socketCallStatus?.status || "-"}
                                                    {" <> "}
                                                    {activeClientId || "-"}
                                                    {" <> "}
                                                    {inboundCallRef?.current?.did || "-"}
                                                    {" <> "}
                                                    {Object.keys(sendMessage || {}).length || "-"}
                                                </small>
                                            </div>
                                        </div> */}
                                        <div className="col-lg-3">
                                            <div>
                                                <Button
                                                    value="Search"
                                                    small
                                                    disabled={["inbound", "wrapping up", "outbound"].includes(status)}
                                                    hideContainer
                                                    onClick={() => {
                                                        setModalData({ toggle: true, type: "vca search client" });
                                                    }}
                                                />{" "}
                                                <Button
                                                    value="Log out"
                                                    className="ml-4"
                                                    color="red"
                                                    small
                                                    disabled={["inbound", "wrapping up", "outbound"].includes(status)}
                                                    hideContainer
                                                    onClick={() => {
                                                        window.location.href = "/logout";
                                                    }}
                                                />{" "}
                                                {Number(user.user_id) === 1 ? (
                                                    <Button
                                                        value="Error"
                                                        className="ml-1"
                                                        color="red"
                                                        small
                                                        outline
                                                        hideContainer
                                                        onClick={() => {
                                                            setActiveClient({ ...activeClient, locations: "EEE" });
                                                        }}
                                                    />
                                                ) : (
                                                    <></>
                                                )}
                                                <Button
                                                    value="Clear spinning"
                                                    className="float-right"
                                                    color="red"
                                                    small
                                                    outline
                                                    hideContainer
                                                    onClick={() => {
                                                        const a = window.confirm("Are you sure you want to clear the spinning?");
                                                        if (a) {
                                                            // The ref too: call_action's double-click guard reads
                                                            // the ref, so clearing only the state left every later
                                                            // click silently ignored.
                                                            buttonActiveRef.current = {
                                                                save_alert: false,
                                                                send_message: false,
                                                                check_customer_alerts: false,
                                                                call_action: {},
                                                            };
                                                            setButtonActive({
                                                                save_alert: false,
                                                                send_message: false,
                                                                check_customer_alerts: false,
                                                                call_action: {},
                                                            });
                                                        }
                                                        return;
                                                    }}
                                                />
                                                {Number(activeClient?.admin_section?.status) === 1 && Number(user.admin_section) === 1 ? (
                                                    <Button
                                                        value="Admin"
                                                        className="ml-1"
                                                        color="orange"
                                                        small
                                                        outline
                                                        hideContainer
                                                        onClick={() => {
                                                            setToggleAdminSection(!toggleAdminSection);
                                                        }}
                                                    />
                                                ) : (
                                                    <></>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-lg-3">
                                            <div>
                                                <div className="small d-inline-block">Status</div>
                                                <div className="fw-600 d-inline-block ml-1">
                                                    {[""].includes(status)
                                                        ? "Available"
                                                        : ["search"].includes(status)
                                                          ? "Busy"
                                                          : ["outbound"].includes(status)
                                                            ? `Outbound call ${timerCallDuration.string}`
                                                            : ["ringing"].includes(status)
                                                              ? "Ringing"
                                                              : ["inbound"].includes(status)
                                                                ? `On call ${timerCallDuration.string}`
                                                                : ["wrapping up"].includes(status)
                                                                  ? `Wrapping up ${timerWrapping.string}`
                                                                  : ""}
                                                    {status === "inbound" ? (
                                                        user.max_call_duration?.end ? (
                                                            <div className="small red">Call will end after {user.max_call_duration?.end || ""}</div>
                                                        ) : (activeClient?.dids || "").length > 0 ? (
                                                            JSON.stringify(activeClient?.dids[activeClient?.dids.findIndex((a: any) => a.did === (inboundCallRef.current.did || outboundCall.did))]?.max_call_duration || {}) !== "{}" ? (
                                                                <div className="small red">
                                                                    Call will end after{" "}
                                                                    {new Misc().to_duration(
                                                                        activeClient?.dids[activeClient?.dids.findIndex((a: any) => a.did === (inboundCallRef.current.did || outboundCall.did))].max_call_duration?.end || "",
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                ""
                                                            )
                                                        ) : (
                                                            ""
                                                        )
                                                    ) : (
                                                        ""
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-lg-3">
                                            <div>
                                                <div className="small d-inline-block">Queue</div>

                                                <div className="fw-600 d-inline-block ml-1">{vca_queue_for_user(socketCallQueue, user.answer_group).length} in queue</div>
                                            </div>
                                        </div>
                                        <div className="col-lg-3">
                                            <div>
                                                <div className="small d-inline-block">Longest in queue</div>
                                                <div className="fw-600 d-inline-block ml-1">{vca_queue_for_user(socketCallQueue, user.answer_group).length > 0 ? timerLongestInQueue : "00:00"}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className={activeClientIdRef.current === 0 ? "d-none" : ""} id="vca-header-2"></div>
                                <div className={activeClientIdRef.current === 0 ? "d-none" : ""} id="vca-header-3">
                                    <div className="col-12">
                                        <div className="row">
                                            <div className="col-lg-6">
                                                <div>
                                                    <div className="small red">Client Alerts</div>
                                                    {(activeClient.alerts || []).map((a: any, i: number) => {
                                                        return <div key={i} dangerouslySetInnerHTML={{ __html: a.alert || "" }}></div>;
                                                    })}
                                                </div>
                                            </div>
                                            <div className="col-lg-6" style={{ backgroundColor: "#FFF" }}>
                                                <div>
                                                    <div className="small red">Caller Alerts</div>
                                                    {(customerAlerts || [])
                                                        .filter((a: any) => a.alert?.alert !== null && a.alert !== null && Number(a.status) === 1)
                                                        .map((a: any, i: number, filteredArray: any[]) => (
                                                            <div key={a.id || i}>
                                                                <div
                                                                    className="small mb-1"
                                                                    style={{
                                                                        borderBottom: i + 1 !== filteredArray.length ? "1px solid RGBA(0,0,0,0.2)" : "",
                                                                    }}>
                                                                    <table>
                                                                        <tbody>
                                                                            <tr>
                                                                                <td className="vertical-align-top">
                                                                                    <b>Name</b> {i + 1} - {(customerAlerts || []).length}
                                                                                </td>
                                                                                <td>
                                                                                    {a.first_name} {a.last_name}
                                                                                </td>
                                                                            </tr>
                                                                            <tr>
                                                                                <td className="vertical-align-top">
                                                                                    <b>Alert</b>
                                                                                </td>
                                                                                <td dangerouslySetInnerHTML={{ __html: he.decode(a.alert.alert || "") }}></td>
                                                                            </tr>
                                                                            <tr>
                                                                                <td>
                                                                                    <Button
                                                                                        value="Completed"
                                                                                        small
                                                                                        onClick={() => {
                                                                                            completed_caller_alert({ account_id: a.account_id, alert_uuid: a.alert.uuid, customer_uuid: a.uuid });
                                                                                        }}
                                                                                    />
                                                                                </td>
                                                                            </tr>
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            </div>
                                                        ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className={activeClientIdRef.current === 0 ? "d-none" : ""} id="vca-header-4">
                                    <div className="row">
                                        {/* <div className="col-lg-6">
                                            <div>
                                                <div className="small red">Caller Alerts</div>
                                                {(customerAlerts || []).map((a: any, i: number) => {
                                                    if (a.alert?.alert === null || a.alert === null || Number(a.status) !== 1) return <React.Fragment key={i} />;
                                                    return (
                                                        <div key={i}>
                                                            <div className="small mb-1">
                                                                <table>
                                                                    <tbody>
                                                                        <tr>
                                                                            <td className="vertical-align-top">
                                                                                <b>Name</b>
                                                                            </td>
                                                                            <td>
                                                                                {a.first_name} {a.lsst_name}
                                                                            </td>
                                                                        </tr>
                                                                        <tr>
                                                                            <td className="vertical-align-top">
                                                                                <b>Alert</b>
                                                                            </td>
                                                                            <td dangerouslySetInnerHTML={{ __html: he.decode(a.alert.alert || "") }}></td>
                                                                        </tr>
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div> */}
                                    </div>
                                </div>
                                <div className={activeClientIdRef.current === 0 ? "d-none" : ""} id="vca-left-body">
                                    <div className="row">
                                        <div className="col-lg-8">
                                            <div id="vca-left-header">
                                                <div className="blue fw-600">{he.decode(activeClient?.name || "")} - Answer Process</div>
                                                <div dangerouslySetInnerHTML={{ __html: he.decode(activeClient.answer_process || "-") }}></div>
                                            </div>
                                            {toggleAdminSection ? (
                                                <div className="row">
                                                    <div id="vca-left-admin-left" ref={(_) => vca_left_body(_, ["#vca-header", "#vca-header-2", "#vca-header-3", "#vca-header-4", "#vca-left-header"])} className="col-lg-6">
                                                        <div dangerouslySetInnerHTML={{ __html: he.decode(activeClient?.admin_section?.left || "-") }}></div>
                                                    </div>
                                                    <div id="vca-left-admin-right" ref={(_) => vca_left_body(_, ["#vca-header", "#vca-header-2", "#vca-header-3", "#vca-header-4", "#vca-left-header"])} className="col-lg-6">
                                                        <div dangerouslySetInnerHTML={{ __html: he.decode(activeClient?.admin_section?.right || "-") }}></div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="row">
                                                    <div id="vca-left-booking" ref={(_) => vca_left_body(_, ["#vca-header", "#vca-header-2", "#vca-header-3", "#vca-header-4", "#vca-left-header"])} className="col-lg-6">
                                                        {/* <div className="blue fw-600">Booking Rules</div> */}
                                                        <div className="blue mt-4 fw-600 text-upper" style={{ padding: "5px 0" }}>
                                                            Logins
                                                        </div>
                                                        <div dangerouslySetInnerHTML={{ __html: he.decode(activeClient?.internal_info?.logins || "-") }}></div>
                                                        <div className="blue mt-4 fw-600 text-upper" style={{ borderTop: "2px solid black", padding: "5px 0" }}>
                                                            Cancellation Policy
                                                        </div>
                                                        <div dangerouslySetInnerHTML={{ __html: he.decode(activeClient?.internal_info?.cancellation_policy || "-") }}></div>
                                                        <div className="blue mt-4 fw-600 text-upper" style={{ borderTop: "2px solid black", padding: "5px 0" }}>
                                                            Same Day Appointment Policy
                                                        </div>
                                                        <div dangerouslySetInnerHTML={{ __html: he.decode(activeClient?.internal_info?.same_day_appt_policy || "-") }}></div>
                                                        <div className="blue mt-4 fw-600 text-upper" style={{ borderTop: "2px solid black", padding: "5px 0" }}>
                                                            Booking Rules
                                                            {/* booking rules = nothing_available */}
                                                        </div>
                                                        <div dangerouslySetInnerHTML={{ __html: he.decode(activeClient?.internal_info?.nothing_available || "-") }}></div>
                                                        <div className="blue mt-4 fw-600 text-upper" style={{ borderTop: "2px solid black", padding: "5px 0" }}>
                                                            Info to Get
                                                        </div>
                                                        <div dangerouslySetInnerHTML={{ __html: he.decode(activeClient?.internal_info?.info_to_get || "-") }}></div>
                                                        <div className="blue mt-4 fw-600 text-upper" style={{ borderTop: "2px solid black", padding: "5px 0" }}>
                                                            Appointment Info
                                                            {/* appointment info = appt_type */}
                                                        </div>
                                                        <div dangerouslySetInnerHTML={{ __html: he.decode(activeClient?.internal_info?.appt_type || "-") }}></div>
                                                        <div className="blue mt-4 fw-600 text-upper" style={{ borderTop: "2px solid black", padding: "5px 0" }}>
                                                            Hours
                                                        </div>
                                                        <div dangerouslySetInnerHTML={{ __html: he.decode(activeClient?.internal_info?.hours || "-") }}></div>
                                                    </div>
                                                    <div id="vca-left-info" ref={(_) => vca_left_body(_, ["#vca-header", "#vca-header-2", "#vca-header-3", "#vca-header-4", "#vca-left-header"])} className="col-lg-6">
                                                        <div className="orange fw-600 text-upper">Info to Give Out</div>
                                                        <div dangerouslySetInnerHTML={{ __html: he.decode(activeClient?.info_to_give_out?.summary || "-") }}></div>
                                                        <div className="orange mt-4 fw-600 text-upper" style={{ borderTop: "2px solid black", padding: "5px 0" }}>
                                                            Services
                                                        </div>
                                                        <div dangerouslySetInnerHTML={{ __html: he.decode(activeClient?.info_to_give_out?.services || "-") }}></div>
                                                        <div className="orange mt-4 fw-600 text-upper" style={{ borderTop: "2px solid black", padding: "5px 0" }}>
                                                            Pricing Info
                                                        </div>
                                                        <div dangerouslySetInnerHTML={{ __html: he.decode(activeClient?.info_to_give_out?.pricing || "-") }}></div>
                                                        <div className="orange mt-4 fw-600 text-upper" style={{ borderTop: "2px solid black", padding: "5px 0" }}>
                                                            Locations
                                                        </div>
                                                        <div dangerouslySetInnerHTML={{ __html: he.decode(activeClient?.info_to_give_out?.location || "-") }}></div>
                                                        <div className="orange mt-4 fw-600 text-upper" style={{ borderTop: "2px solid black", padding: "5px 0" }}>
                                                            FAQs
                                                        </div>
                                                        <div dangerouslySetInnerHTML={{ __html: he.decode(activeClient?.info_to_give_out?.faqs || "-") }}></div>
                                                        <div className="orange mt-4 fw-600 text-upper" style={{ borderTop: "2px solid black", padding: "5px 0" }}>
                                                            Other Info
                                                        </div>
                                                        <div dangerouslySetInnerHTML={{ __html: he.decode(activeClient?.info_to_give_out?.other || "-") }}></div>
                                                        <div className="orange mt-4 fw-600 text-upper" style={{ borderTop: "2px solid black", padding: "5px 0" }}>
                                                            Address
                                                        </div>
                                                        <div>
                                                            <Button
                                                                small
                                                                value="Find closest location"
                                                                onClick={() => {
                                                                    setModalData({ toggle: true, type: "location search", data: activeClient });
                                                                }}
                                                            />
                                                            {(activeClient?.locations || []).map((a: any, i: number) => {
                                                                if (Number(a.status) !== 1) return <React.Fragment key={i} />;
                                                                return (
                                                                    <div key={i}>
                                                                        <div>{a.name}</div>
                                                                        <div>
                                                                            <u>Address</u>
                                                                        </div>
                                                                        <div className="mb-2">
                                                                            {a.address} {a.city} {a.state} {a.postcode}
                                                                        </div>
                                                                        <div>
                                                                            <u>Postal Address</u>
                                                                        </div>
                                                                        <div>
                                                                            {a.postal_address} {a.postal_city} {a.postal_state} {a.postal_postcode}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <div id="vca-left-message" ref={(_) => vca_left_body(_, ["#vca-header", "#vca-header-2", "#vca-header-3", "#vca-header-4"])} className="col-lg-4">
                                            {/* <div className="row">
                                                <div className="col-3">
                                                    <Button className="small" color="green" value="New" />
                                                </div>
                                                <div className="col-3">
                                                    <Button className="small" color="green" value="New" />
                                                </div>
                                                <div className="col-3">
                                                    <Button className="small" color="green" value="New" />
                                                </div>
                                            </div> */}
                                            <div className="fw-600 mt-4">Message</div>

                                            <div className="row">
                                                {" "}
                                                <div className="col-12">
                                                    <div className="row">
                                                        <div className="col-6">
                                                            <div className="mt-4 fw-500">Caller Name</div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-6">
                                                    <Input
                                                        container={false}
                                                        defaultValue={sendMessage.caller_first_name || ""}
                                                        changeDefault={new Date().getTime()}
                                                        name="messagefirstname"
                                                        type="text"
                                                        placeholder="First name"
                                                        className="input-small"
                                                        updateChange={(v) => {
                                                            setSendMessage({ ...sendMessage, caller_first_name: v });
                                                        }}
                                                    />
                                                </div>
                                                <div className="col-6">
                                                    <Input
                                                        container={false}
                                                        defaultValue={sendMessage.caller_last_name || ""}
                                                        changeDefault={new Date().getTime()}
                                                        name="messagelastname"
                                                        type="text"
                                                        placeholder="Last name"
                                                        className="input-small"
                                                        updateChange={(v) => {
                                                            setSendMessage({ ...sendMessage, caller_last_name: v });
                                                        }}
                                                    />
                                                </div>
                                                <div className="col-12">
                                                    <div className="mt-4 fw-500">Caller Phone Number</div>
                                                </div>
                                                <div className="col-12">
                                                    <Input
                                                        required
                                                        container={false}
                                                        defaultValue={sendMessage.caller_number || ""}
                                                        changeDefault={new Date().getTime()}
                                                        name="messagecallernumber"
                                                        type="text"
                                                        placeholder="Caller phone number"
                                                        className="input-small"
                                                        updateChange={(v) => {
                                                            setSendMessage({ ...sendMessage, caller_number: v });
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                            <div className="mt-4 fw-500">Send to</div>
                                            <Input
                                                required
                                                container={false}
                                                readOnly
                                                defaultValue={sendMessage.to_name || ""}
                                                changeDefault={new Date().getTime()}
                                                name="sendto"
                                                type="text"
                                                placeholder="Select from contacts list"
                                                className="input-small"
                                            />
                                            <div className="row">
                                                <div className={["wrapping up", "inbound", "outbound"].includes(status) ? "col-12 p-4" : "d-none"}>
                                                    <div className="search-caller">
                                                        <div className="row">
                                                            <div className="col-12">
                                                                <div className="fw-500">Search for Caller</div>
                                                            </div>
                                                            <div className="col-12">
                                                                <Select
                                                                    options={(status === "inbound" ? inboundCallRef.current?.caller_search || [] : outboundCall?.caller_search || []).map((a: any) => {
                                                                        return { label: a.first_name + " " + a.last_name, value: a.uuid };
                                                                    })}
                                                                    defaultValue={sendMessage?.customer_uuid || ""}
                                                                    name="customeruuid"
                                                                    placeholder="Select"
                                                                    className="input-small"
                                                                    container={false}
                                                                    updateChange={(v) => {
                                                                        let _caller_first_name = "",
                                                                            _caller_last_name = "",
                                                                            _caller_number = "";
                                                                        if (v) {
                                                                            const arr = status === "inbound" ? inboundCallRef.current?.caller_search : outboundCall?.caller_search;
                                                                            for (let index = 0; index < (arr || []).length; index++) {
                                                                                if (arr[index].uuid === v) {
                                                                                    _caller_first_name = arr[index].first_name;
                                                                                    _caller_last_name = arr[index].last_name;
                                                                                    _caller_number = arr[index].prefix + arr[index].phone_number;
                                                                                    break;
                                                                                }
                                                                            }
                                                                        }
                                                                        setSendMessage({
                                                                            ...sendMessage,
                                                                            customer_uuid: v,
                                                                            caller_first_name: _caller_first_name,
                                                                            caller_last_name: _caller_last_name,
                                                                            caller_number: _caller_number,
                                                                            searched_caller_number: _caller_number,
                                                                        });
                                                                    }}
                                                                />
                                                            </div>
                                                            <div className="col-12">
                                                                <Input
                                                                    // container={false}
                                                                    readOnly
                                                                    defaultValue={sendMessage.searched_caller_number || ""}
                                                                    changeDefault={new Date().getTime()}
                                                                    name="messagesearchcutsomernumber"
                                                                    type="text"
                                                                    placeholder="Caller phone number"
                                                                    className="input-small"
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* {![1, 3].includes(Number(user.user_id)) ? (
                                                    <></>
                                                ) : ( */}
                                                <ConditionalForm
                                                    form={activeClient?.conditional_form}
                                                    update_answers={(v: any) => {
                                                        setConditionalFormAnswers(v);
                                                    }}
                                                    update_full_answers={(v: any) => {
                                                        setConditionalFormAnswersFull(v);
                                                    }}
                                                    update_current_question_contacts={(contacts: string[]) => {
                                                        setCurrentQuestionContacts(contacts);
                                                    }}
                                                    clear={JSON.stringify(conditionalFormAnswers) === "[]"}
                                                />
                                                {/* )} */}
                                                <div className="col-12">
                                                    <div className="mt-4 fw-500">Subject of Email</div>
                                                </div>
                                                <div className="col-12">
                                                    <Input
                                                        required
                                                        container={false}
                                                        defaultValue={sendMessage.subject || ""}
                                                        changeDefault={new Date().getTime()}
                                                        name="messagesubject"
                                                        type="text"
                                                        placeholder="Subject"
                                                        className="input-small"
                                                        updateChange={(v) => {
                                                            setSendMessage({ ...sendMessage, subject: v });
                                                        }}
                                                    />
                                                </div>
                                                <div className={Number(activeClient.show_3rd_party || 0) === 1 ? "col-12" : "d-none"}>
                                                    <div className="mt-4 fw-500">Actions in Call</div>
                                                </div>
                                                <div className={Number(activeClient.show_3rd_party || 0) === 1 ? "col-12" : "d-none"}>
                                                    <Select
                                                        required
                                                        options={[
                                                            { label: "Yes", value: "1" },
                                                            { label: "No", value: "0" },
                                                        ].map((a: any) => {
                                                            return { label: a.label, value: a.value };
                                                        })}
                                                        defaultValue={sendMessage.third_party_action || ""}
                                                        name="message3pa"
                                                        placeholder="Select"
                                                        className="input-small"
                                                        container={false}
                                                        updateChange={(v) => {
                                                            setSendMessage({ ...sendMessage, third_party_action: v });
                                                        }}
                                                    />
                                                </div>
                                            </div>

                                            {((activeClient.forms || []).length > 0 ? activeClient.forms[0].form.form.form || [] : []).map((a: any, i: number) => {
                                                if (Number(activeClient?.conditional_form?.status || 0) === 1) return <React.Fragment key={i} />;

                                                if (a.type === "multiplechoice") {
                                                    return (
                                                        <div key={i}>
                                                            <div className="mt-4 fw-500">{he.decode(a?.label || "")}</div>
                                                            {(a?.properties?.items || []).map((b: any, ii: number) => {
                                                                return (
                                                                    <div key={ii} className="mb-2">
                                                                        <input
                                                                            type="checkbox"
                                                                            id={`radio-${ii}`}
                                                                            value={b}
                                                                            checked={((sendMessage?.custom || "").length > 0 ? sendMessage?.custom?.[i]?.[he.decode(a?.label || "")]?.value || [] : []).includes(b)}
                                                                            onChange={() => {
                                                                                /*leave onchange to fix browser error*/
                                                                            }}
                                                                            onClick={(e: any) => {
                                                                                const v = e.target?.value,
                                                                                    c = e.target?.checked;

                                                                                let arr = sendMessage?.custom || [],
                                                                                    new_custom = [];
                                                                                for (let index = 0; index < (arr || []).length; index++) {
                                                                                    if (index === i) {
                                                                                        let _arr = arr[index].value || [];
                                                                                        if (c === true) {
                                                                                            if (!_arr.includes(_arr)) _arr.push(v);
                                                                                        } else {
                                                                                            _arr = _arr.filter((_item: any) => _item !== v);
                                                                                        }
                                                                                        new_custom.push({ ...arr[index], [he.decode(a?.label || "")]: { ...arr[index][he.decode(a?.label || "")], value: _arr } });
                                                                                    } else {
                                                                                        new_custom.push(arr[index]);
                                                                                    }
                                                                                }

                                                                                setSendMessage({ ...sendMessage, custom: new_custom });
                                                                            }}
                                                                        />{" "}
                                                                        <div className="d-inline-block">{b}</div>
                                                                    </div>
                                                                );
                                                            })}
                                                            {a.mandatory === true && ((sendMessage?.custom || "").length > 0 ? sendMessage?.custom[i]?.[he.decode(a?.label || "")]?.value || "" : "").length === 0 ? (
                                                                <div className="red">{he.decode(a?.label || "")} is required</div>
                                                            ) : (
                                                                ""
                                                            )}
                                                        </div>
                                                    );
                                                }
                                                if (a.type === "singlechoice") {
                                                    return (
                                                        <div key={i}>
                                                            <div className="mt-4 fw-500">{he.decode(a?.label || "")}</div>
                                                            <Select
                                                                container={false}
                                                                options={(a?.properties?.items || []).map((a: any) => {
                                                                    return { label: a, value: a };
                                                                })}
                                                                name={`message${i}`}
                                                                placeholder={he.decode(a?.label || "")}
                                                                className="input-small"
                                                                defaultValue={(sendMessage?.custom || "").length > 0 ? sendMessage?.custom?.[i]?.[he.decode(a?.label || "")]?.value || "" : ""}
                                                                updateChange={(v) => {
                                                                    let arr = sendMessage?.custom || [],
                                                                        new_custom = [];
                                                                    for (let index = 0; index < arr.length; index++) {
                                                                        if (index === i) {
                                                                            new_custom.push({ ...arr[index], [he.decode(a?.label || "")]: { ...arr[index][he.decode(a?.label || "")], value: v } });
                                                                        } else {
                                                                            new_custom.push(arr[index]);
                                                                        }
                                                                    }

                                                                    setSendMessage({ ...sendMessage, custom: new_custom });
                                                                }}
                                                            />
                                                            {a.mandatory === true && ((sendMessage?.custom || "").length > 0 ? sendMessage?.custom?.[i]?.[he.decode(a?.label || "")]?.value || "" : "").length === 0 ? (
                                                                <div className="red">{he.decode(a?.label || "")} is required</div>
                                                            ) : (
                                                                ""
                                                            )}
                                                        </div>
                                                    );
                                                }
                                                if (a.type === "number") {
                                                    return (
                                                        <div key={i}>
                                                            <div className="mt-4 fw-500">{he.decode(a?.label || "")}</div>
                                                            <Input
                                                                container={false}
                                                                type="number"
                                                                name={`message${i}`}
                                                                placeholder={he.decode(a?.label || "")}
                                                                className="input-small"
                                                                defaultValue={(sendMessage?.custom || "").length > 0 ? sendMessage?.custom?.[i]?.[he.decode(a?.label || "")]?.value || "" : ""}
                                                                changeDefault={new Date().getTime()}
                                                                updateChange={(v) => {
                                                                    let arr = sendMessage?.custom || [],
                                                                        new_custom = [];
                                                                    for (let index = 0; index < arr.length; index++) {
                                                                        if (index === i) {
                                                                            new_custom.push({ ...arr[index], [he.decode(a?.label || "")]: { ...arr[index][he.decode(a?.label || "")], value: v } });
                                                                        } else {
                                                                            new_custom.push(arr[index]);
                                                                        }
                                                                    }

                                                                    setSendMessage({ ...sendMessage, custom: new_custom });
                                                                }}
                                                            />
                                                            {a.mandatory === true && ((sendMessage?.custom || "").length > 0 ? sendMessage?.custom?.[i]?.[he.decode(a?.label || "")]?.value || "" : "").length === 0 ? (
                                                                <div className="red">{he.decode(a?.label || "")} is required</div>
                                                            ) : (
                                                                ""
                                                            )}
                                                        </div>
                                                    );
                                                }
                                                if (a.type === "text") {
                                                    return (
                                                        <div key={i}>
                                                            <div className="mt-4 fw-500">{he.decode(a?.label || "")}</div>
                                                            <Textarea
                                                                container={false}
                                                                autoResize={true}
                                                                name={`message${i}`}
                                                                placeholder={he.decode(a?.label || "")}
                                                                defaultValue={(sendMessage?.custom || "").length > 0 ? sendMessage?.custom?.[i]?.[he.decode(a?.label || "")]?.value || "" : ""}
                                                                changeDefault={new Date().getTime()}
                                                                updateChange={(v) => {
                                                                    let arr = sendMessage?.custom || [],
                                                                        new_custom = [];
                                                                    for (let index = 0; index < arr.length; index++) {
                                                                        if (index === i) {
                                                                            new_custom.push({ ...arr[index], [he.decode(a?.label || "")]: { ...arr[index][he.decode(a?.label || "")], value: v } });
                                                                        } else {
                                                                            new_custom.push(arr[index]);
                                                                        }
                                                                    }

                                                                    setSendMessage({ ...sendMessage, custom: new_custom });
                                                                }}
                                                            />
                                                            {a.mandatory === true && ((sendMessage?.custom || "").length > 0 ? sendMessage?.custom?.[i]?.[he.decode(a?.label || "")]?.value || "" : "").length === 0 ? (
                                                                <div className="red">{he.decode(a?.label || "")} is required</div>
                                                            ) : (
                                                                ""
                                                            )}
                                                        </div>
                                                    );
                                                }
                                                if (a.type === "section") {
                                                    return (
                                                        <div key={i}>
                                                            <div className="mt-4 fw-600">{a.properties.heading}</div>
                                                        </div>
                                                    );
                                                }
                                                if (a.type === "instruction") {
                                                    return (
                                                        <div key={i}>
                                                            <div className="mt-4 fw-500">{he.decode(a?.label || "")}</div>
                                                            <div className="">{a.properties.text}</div>
                                                        </div>
                                                    );
                                                }

                                                return <React.Fragment key={i} />;
                                            })}

                                            <div className="text-right">
                                                <Button
                                                    value="Submit"
                                                    active={buttonActive.send_message}
                                                    onClick={() => {
                                                        send_message();
                                                    }}
                                                    small
                                                />
                                            </div>
                                            {/*  */}
                                            <div
                                                className={
                                                    ["outbound", "search"].includes(statusRef.current) ||
                                                    (statusRef.current === "wrapping up" && (outboundCall.destination || "").length !== 0 && (outboundCall.caller_prefix || "").length !== 0)
                                                        ? " my-4"
                                                        : "d-none"
                                                }
                                                style={{ borderTop: "1px solid #ccc" }}>
                                                <div className="fw-600 mt-4">Create customer alert</div>
                                                <Textarea
                                                    autoResize={true}
                                                    name="createcustomeralert"
                                                    placeholder="Alert"
                                                    updateChange={(v: any) => {
                                                        setCreateCustomerAlert(v);
                                                    }}
                                                />
                                                <div className="text-right">
                                                    <Button
                                                        value="Save alert"
                                                        disabled={(outboundCall.destination || "").length === 0 || (outboundCall.caller_prefix || "").length === 0}
                                                        onClick={() => {
                                                            if ((outboundCall.destination || "").length === 0 || (outboundCall.caller_prefix || "").length === 0) return;
                                                            save_alert();
                                                        }}
                                                        active={buttonActive.save_alert}
                                                        small
                                                        hideContainer
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xxl-2 col-lg-3 pl-0" style={{ overflowY: "auto" }}>
                            {!["search", "inbound", "wrapping up", "outbound"].includes(statusRef.current) ? (
                                <div
                                    className="row mt-2"
                                    style={{
                                        border: "none",
                                        backgroundColor: "transparent",
                                        padding: 0,
                                        marginBottom: "15px",
                                    }}>
                                    <div className="col-12">
                                        <Button
                                            value="On a break"
                                            small
                                            color="blue"
                                            hideContainer
                                            className="w-100"
                                            onClick={() => {
                                                on_a_break("break");
                                            }}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <></>
                            )}
                            {statusRef.current === "ringing" ? (
                                <div id="vca-right">
                                    <div id="vca-right-phone-inbound" className="d-none">
                                        {/* <div className="small">Client Name</div>
                                        <div className="fw-600">{activeClient?.name || "-"}</div> */}
                                        {/* <div className="small mt-2">Caller Name</div>
                                        <div className="fw-600">{inboundCallRef.current?.caller_name || "-"}</div>
                                        <div className="small mt-2">CLI</div>
                                        <div className="fw-600">
                                            {(activeClient.dids || []).map((a: any, i: number) => {
                                                if (a.did === inboundCallRef.current.did && Number(a.hide_cli) === 0) {
                                                    return inboundCallRef.current.from;
                                                }
                                                return <></>;
                                            })}
                                        </div> */}
                                        {/* 
                                        OLD REPLACED
                                        <Button
                                            color="green"
                                            onClick={() => {
                                                phone.answer();
                                            }}
                                            value="Accept call"
                                            className="w-100 mt-2"
                                            hideContainer
                                        /> */}
                                    </div>
                                    {/* */}
                                    <div className="col-12">
                                        <div className="row">
                                            <div className="col-12">
                                                <div className="small">Client Name</div>
                                                <div dangerouslySetInnerHTML={{ __html: activeClient.name }}></div>
                                                <div dangerouslySetInnerHTML={{ __html: `<b>${activeClient?.service_type}</b>` }}></div>
                                            </div>
                                        </div>
                                    </div>
                                    {/*  */}
                                    <div className="col-12">
                                        <div className="row">
                                            <div className="col-12">
                                                <Button
                                                    className="w-100 mt-2"
                                                    color="green"
                                                    hideContainer
                                                    value="Accept Call"
                                                    onClick={async () => {
                                                        answer_call_v2();
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    {/*  */}
                                    <div className="col-12">
                                        {" "}
                                        <div className={statusRef.current !== "inbound" ? "row" : "d-none"}>
                                            <div className="col-lg-12">
                                                <div>
                                                    <div className="small green">Answer Script</div>
                                                    <div>
                                                        {(() => {
                                                            // If no DIDs available, return empty string
                                                            if (!activeClient?.dids?.length) return "";

                                                            // If not in the right call status or no call data, return the first DID's welcome script
                                                            if (!["ringing", "inbound"].includes(status) || JSON.stringify(inboundCallRef.current || outboundCall) === "{}") {
                                                                return activeClient?.dids?.[0]?.welcome_script || "";
                                                            }

                                                            // Find the correct DID safely
                                                            const currentDid = inboundCallRef.current?.did || outboundCall?.did;
                                                            const didIndex = activeClient.dids.findIndex((a: any) => a.did === currentDid);

                                                            // Get the DID object (or fallback to the first one if not found)
                                                            const did = didIndex >= 0 ? activeClient.dids[didIndex] : activeClient.dids[0];

                                                            // Get timezone safely
                                                            const timezone = did?.tz || "Australia/Sydney";

                                                            // Get time of day
                                                            const hour = moment.tz(timezone).hour();
                                                            const timeOfDay = hour < 12 ? "morning" : "afternoon";

                                                            // Return the formatted welcome script
                                                            return he
                                                                .decode(did?.welcome_script || "")
                                                                .replace("<agent_name>", user.first_name)
                                                                .replace("<time_of_day>", timeOfDay);
                                                        })()}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {/*  */}

                                    <div className="col-12">
                                        <div className="row">
                                            <div className="col-12">
                                                <div className="small mt-2">Caller Name</div>
                                                <div className="--fw-600">{inboundCallRef.current?.caller_name || "-"}</div>
                                                <div className="small mt-2">Callers Number</div>
                                                <div className="--fw-600">
                                                    {(activeClient.dids || []).map((a: any, i: number) => {
                                                        if (a.did === inboundCallRef.current.did && Number(a.hide_cli) === 0) {
                                                            return inboundCallRef.current.from;
                                                        }
                                                        return <></>;
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <></>
                            )}

                            <div id="vca-right">
                                {["ringing", ""].includes(statusRef.current) ? (
                                    <></>
                                ) : (
                                    <>
                                        <div
                                            className="row"
                                            style={{
                                                border: "none",
                                                backgroundColor: "transparent",
                                                padding: 0,
                                                marginBottom: "15px",
                                            }}>
                                            {/*  */}
                                            <div className={["search", "wrapping up"].includes(statusRef.current) ? "col-lg-12 text-right" : "d-none"}>
                                                {["wrapping up"].includes(statusRef.current) ? (
                                                    <>
                                                        <Button
                                                            value="Close Client Info & Close Screen"
                                                            active={buttonActive.call_action["wrapping up done"]}
                                                            small
                                                            color="red"
                                                            className="w-100"
                                                            onClick={() => {
                                                                if (messageSent !== true) {
                                                                    const a = window.confirm("Are you sure you want to close screen without sending a message?");
                                                                    if (a) {
                                                                        call_action("wrapping up done");
                                                                    }
                                                                    return;
                                                                }
                                                                call_action("wrapping up done");
                                                            }}
                                                        />

                                                        <div className={(inboundCallRef.current.participant_caller_call_sid || outboundCall.participant_caller_call_sid) && messageSent !== true && endCallSeconds < 20 ? "row" : "d-none"}>
                                                            <div className="col-12">
                                                                <Button
                                                                    value="No Caller & Close Screen"
                                                                    active={buttonActive.call_action["no caller"]}
                                                                    small
                                                                    color="blue"
                                                                    className="w-100"
                                                                    onClick={() => {
                                                                        call_action("no caller", { did: inboundCallRef.current.did, conf_sid: inboundCallRef.current.conference_sid });
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Button
                                                            color="red"
                                                            small
                                                            hideContainer
                                                            value="Close client info / close screen"
                                                            onClick={() => {
                                                                setActiveClientId(0);
                                                                setActiveClient({});
                                                                setSendMessage({});
                                                                setConditionalFormAnswers([]);
                                                                setCustomerAlerts([]);
                                                                // setOutboundCall({ ...outboundCall, caller_prefix: "+61" });
                                                                // outboundCallRef.current = {
                                                                //     ...outboundCall,
                                                                //     caller_prefix: "+61",
                                                                // };
                                                                setOutboundCall({ caller_prefix: "+61" });
                                                                outboundCallRef.current = {
                                                                    caller_prefix: "+61",
                                                                };
                                                                setStatus("");
                                                                statusRef.current = "";
                                                                setMessageSent({});
                                                                activeClientIdRef.current = 0;
                                                            }}
                                                        />
                                                    </>
                                                )}
                                            </div>
                                            {/*  */}
                                        </div>
                                        {/* */}
                                        <div className={activeClientIdRef.current === 0 ? "d-none" : "col-12"}>
                                            <div className="row">
                                                <div className="col-12">
                                                    <div className="small">Client Name</div>
                                                    <div dangerouslySetInnerHTML={{ __html: activeClient.name }}></div>
                                                    <div dangerouslySetInnerHTML={{ __html: `<b>${activeClient?.service_type}</b>` }}></div>
                                                </div>
                                            </div>
                                        </div>
                                        {/*  */}
                                        {["inbound", "wrapping up"].includes(statusRef.current) ? (
                                            statusRef.current === "wrapping up" && activeClientIdRef.current === 0 ? (
                                                <></>
                                            ) : (
                                                <div className="col-12">
                                                    <div className="row">
                                                        <div className="col-12">
                                                            <div className="small mt-2">Caller Name</div>
                                                            <div className="--fw-600">{inboundCallRef.current?.caller_name || "-"}</div>
                                                            <div className="small mt-2">Callers Number</div>
                                                            <div className="--fw-600">
                                                                {(activeClient.dids || []).map((a: any, i: number) => {
                                                                    if (a.did === inboundCallRef.current.did && Number(a.hide_cli) === 0) {
                                                                        return inboundCallRef.current.from;
                                                                    }
                                                                    return <></>;
                                                                })}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                        ) : (
                                            <></>
                                        )}
                                        {/*  */}
                                        <div className={!["wrapping up"].includes(statusRef.current) ? "col-12" : "d-none"}>
                                            {" "}
                                            <div className="row">
                                                <div className="col-lg-12">
                                                    <div>
                                                        <div className="small green">Answer Script</div>
                                                        <div>
                                                            {(() => {
                                                                // If no DIDs available, return empty string
                                                                if (!activeClient?.dids?.length) return "";

                                                                // If not in the right call status or no call data, return the first DID's welcome script
                                                                if (!["ringing", "inbound"].includes(status) || JSON.stringify(inboundCallRef.current || outboundCall) === "{}") {
                                                                    return activeClient?.dids?.[0]?.welcome_script || "";
                                                                }

                                                                // Find the correct DID safely
                                                                const currentDid = inboundCallRef.current?.did || outboundCall?.did;
                                                                const didIndex = activeClient.dids.findIndex((a: any) => a.did === currentDid);

                                                                // Get the DID object (or fallback to the first one if not found)
                                                                const did = didIndex >= 0 ? activeClient.dids[didIndex] : activeClient.dids[0];

                                                                // Get timezone safely
                                                                const timezone = did?.tz || "Australia/Sydney";

                                                                // Get time of day
                                                                const hour = moment.tz(timezone).hour();
                                                                const timeOfDay = hour < 12 ? "morning" : "afternoon";

                                                                // Return the formatted welcome script
                                                                return he
                                                                    .decode(did?.welcome_script || "")
                                                                    .replace("<agent_name>", user.first_name)
                                                                    .replace("<time_of_day>", timeOfDay);
                                                            })()}
                                                            {/* {(activeClient.dids || []).length > 0
                                                        ? ["ringing", "inbound"].includes(status) && JSON.stringify(inboundCallRef.current || outboundCall) !== "{}"
                                                            ? he
                                                                  .decode(activeClient?.dids[activeClient?.dids.findIndex((a: any) => a.did === (inboundCallRef.current.did || outboundCall.did))]?.welcome_script || "")
                                                                  .replace("<agent_name>", user.first_name)
                                                                  .replace(
                                                                      "<time_of_day>",
                                                                      Number(
                                                                          moment.tz(activeClient?.dids[activeClient?.dids.findIndex((a: any) => a.did === (inboundCallRef.current.did || outboundCall.did))].tz || "Australia/Sydney").hour()
                                                                      ) < 12
                                                                          ? "morning"
                                                                          : "afternoon"
                                                                  )
                                                            : activeClient?.dids?.[0]?.welcome_script
                                                        : ""} */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div id="vca-right-phone-inbound" className={["wrapping up"].includes(statusRef.current) ? "d-none" : ""}>
                                            <div className={!["inbound", "outbound"].includes(status) ? "d-none" : "row"}>
                                                <div className="col-12">
                                                    <h4 className="text-center mb-2">
                                                        {status === "inbound"
                                                            ? (activeClient.dids || []).map((a: any, i: number) => {
                                                                  if (a.did === inboundCallRef.current.did && Number(a.hide_cli) === 0) {
                                                                      return inboundCallRef.current.from;
                                                                  }
                                                                  return <></>;
                                                              })
                                                            : outboundCall.to}
                                                    </h4>
                                                    <div className="text-center small mb-4">{timerCallDuration.string}</div>
                                                </div>
                                                <div className="col-12 p-0">
                                                    <div className="small">Call participants</div>
                                                    <div className="call-participants-card mb-1">
                                                        <table className="w-100">
                                                            <tbody>
                                                                <tr>
                                                                    <td className="fw-600">Caller</td>
                                                                    <td>{_he_rounds(status === "inbound" ? inboundCallRef.current.caller_name || "-" : outboundCall.caller_name || "-")}</td>
                                                                    <td>{timerCallDuration.string}</td>
                                                                    <td></td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-600">Agent</td>
                                                                    <td>{_he_rounds(user.first_name || "-")}</td>
                                                                    <td>{timerAgentCallDuration.string}</td>
                                                                    <td>
                                                                        {buttonActive.call_action.mute ? (
                                                                            <>
                                                                                <Loader type="circle" />
                                                                            </>
                                                                        ) : (
                                                                            <div
                                                                                className="fake-href"
                                                                                onClick={() => {
                                                                                    if (buttonActive.call_action.mute) return;
                                                                                    call_action("mute v2", {
                                                                                        mute: Number((status === "inbound" ? inboundCallRef.current.participant_agent_muted : outboundCall.participant_agent_muted) || 0) === 1 ? 0 : 1,
                                                                                        participant: "agent",
                                                                                    });
                                                                                }}>
                                                                                {Number(status === "inbound" ? inboundCallRef.current.participant_agent_muted : outboundCall.participant_agent_muted) === 1 ? "Unmute" : "Mute"}
                                                                            </div>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                                {status === "inbound" ? (
                                                                    <tr className={[1, 2].includes(Number(inboundCallRef.current.participant_client_status || 0)) ? "" : "d-none"}>
                                                                        <td className="fw-600">Client</td>
                                                                        <td></td>
                                                                        <td>{Number(inboundCallRef.current.participant_client_status) === 2 ? "Ringing" : timerClientCallDuration.string}</td>
                                                                        <td></td>
                                                                    </tr>
                                                                ) : (
                                                                    <tr className={[1, 2].includes(Number(outboundCall.participant_client_status || 0)) ? "" : "d-none"}>
                                                                        <td className="fw-600">Client</td>
                                                                        <td></td>
                                                                        <td>{Number(outboundCall.participant_client_status) === 2 ? "Ringing" : timerClientCallDuration.string}</td>
                                                                        <td></td>
                                                                    </tr>
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                    <div className="col-12 p-0">
                                                        <Button
                                                            hideContainer
                                                            value={
                                                                (inboundCallRef.current.participant_client_status || outboundCallRef.current.participant_client_status || 0) === 2
                                                                    ? "Calling Client"
                                                                    : (inboundCallRef.current.participant_client_status || outboundCallRef.current.participant_client_status || 0) === 1
                                                                      ? "Talking to Client"
                                                                      : (inboundCallRef.current.participant_caller_status || outboundCallRef.current.participant_caller_status || 0) === 1
                                                                        ? "Talking to Caller"
                                                                        : "No status"
                                                            }
                                                            small
                                                            color="green"
                                                            className="w-100"
                                                            onClick={() => {}}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* <div className={!["wrapping up"].includes(status) ? "d-none" : "row"}>
                                            <div className="col-12 p-0">
                                                <Button
                                                    value="Close screen"
                                                    active={buttonActive.call_action["wrapping up done"]}
                                                    small
                                                    color="red"
                                                    className="w-100"
                                                    onClick={() => {
                                                        if (messageSent !== true) {
                                                            const a = window.confirm("Are you sure you want to close screen without sending a message?");
                                                            if (a) {
                                                                call_action("wrapping up done");
                                                            }
                                                            return;
                                                        }
                                                        call_action("wrapping up done");
                                                    }}
                                                />
                                            </div>
                                        </div> */}

                                            <div className={!["inbound", "outbound"].includes(status) ? "d-none" : "row"}>
                                                {showKeyPad ? (
                                                    <div id="vca-right-keypad" className="col-12 p-0">
                                                        <div className="row">
                                                            <div className="col-4">
                                                                <span onClick={() => handleSendDigit("1")}>1</span>
                                                            </div>
                                                            <div className="col-4">
                                                                <span onClick={() => handleSendDigit("2")}>2</span>
                                                            </div>
                                                            <div className="col-4">
                                                                <span onClick={() => handleSendDigit("3")}>3</span>
                                                            </div>
                                                        </div>
                                                        <div className="row">
                                                            <div className="col-4">
                                                                <span onClick={() => handleSendDigit("4")}>4</span>
                                                            </div>
                                                            <div className="col-4">
                                                                <span onClick={() => handleSendDigit("5")}>5</span>
                                                            </div>
                                                            <div className="col-4">
                                                                <span onClick={() => handleSendDigit("6")}>6</span>
                                                            </div>
                                                        </div>
                                                        <div className="row">
                                                            <div className="col-4">
                                                                <span onClick={() => handleSendDigit("7")}>7</span>
                                                            </div>
                                                            <div className="col-4">
                                                                <span onClick={() => handleSendDigit("8")}>8</span>
                                                            </div>
                                                            <div className="col-4">
                                                                <span onClick={() => handleSendDigit("9")}>9</span>
                                                            </div>
                                                        </div>
                                                        <div className="row">
                                                            <div className="col-4">
                                                                <span onClick={() => handleSendDigit("*")}>*</span>
                                                            </div>
                                                            <div className="col-4">
                                                                <span onClick={() => handleSendDigit("0")}>0</span>
                                                            </div>
                                                            <div className="col-4">
                                                                <span onClick={() => handleSendDigit("#")}>#</span>
                                                            </div>
                                                            <div className="col-12 p-0 mb-4">
                                                                <Button
                                                                    value="Hide keypad"
                                                                    small
                                                                    className="w-100"
                                                                    onClick={() => {
                                                                        setShowKeyPad(false);
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <div className="col-12 p-0">
                                                            <Button
                                                                value="Show keypad"
                                                                small
                                                                className="w-100"
                                                                onClick={() => {
                                                                    setShowKeyPad(true);
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="col-12 p-0">
                                                            <Button
                                                                value="End call"
                                                                small
                                                                color="red"
                                                                className="w-100"
                                                                disabled={statusRef.current === "outbound" && phone.call === "idle"}
                                                                onClick={async () => {
                                                                    // Mid-transfer this completes it: caller off
                                                                    // hold, then only the agent's own leg ends.
                                                                    await conf_prepare_leave();
                                                                    if (statusRef.current === "outbound") {
                                                                        // A SIP BYE tears down both legs, so the
                                                                        // separate Twilio force-end call isn't needed.
                                                                        phone.hangup();

                                                                        const params = new URLSearchParams(window.location.search);
                                                                        const bhvm_sid = params.get("bhvm_sid");

                                                                        params.delete("bhvm_sid");

                                                                        window.history.replaceState(null, "", window.location.pathname);
                                                                        await new fetch(`/user/vca/bhvm-start-call?call_sid=${bhvm_sid}&action=close`, {}, { success: false, error: true }).get();
                                                                    } else {
                                                                        setEndCallSeconds(Number((new Date().getTime() - timerCallDuration.ts) / 1000));
                                                                        phone.hangup();
                                                                    }
                                                                }}
                                                            />
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                            <div className={["inbound", "outbound", "ringing"].includes(statusRef.current) ? "row" : "d-none"}>
                                                {/* <div className="col-3 px-1">
                                                <div
                                                    className={
                                                        status === "inbound"
                                                            ? JSON.stringify(inboundCallRef.current) !== "{}" && inboundCallRef.current?.last_call_attempt
                                                                ? "call-action-btn-active"
                                                                : "call-action-btn-inactive"
                                                            : status === "outbound"
                                                            ? JSON.stringify(outboundCall) !== "{}" && outboundCall?.last_call_attempt
                                                                ? "call-action-btn-active"
                                                                : "call-action-btn-inactive"
                                                            : "call-action-btn-inactive"
                                                    }
                                                    onClick={() => {
                                                        if (!["inbound", "outbound"].includes(status)) return;
                                                        if (status === "inbound" && !inboundCallRef.current?.last_call_attempt) return;
                                                        if (status === "outbound" && !outboundCall?.last_call_attempt) return;

                                                        call_action(status === "inbound" ? "make call v2" : "make call outbound", {
                                                            phone_number: status === "inbound" ? inboundCallRef.current.last_call_attempt : outboundCall.last_call_attempt,
                                                        });
                                                    }}
                                                >
                                                    {buttonActive.call_action[status === "inbound" ? "make call v2" : "make call outbound"] ? <Loader type="circle" /> : "Call client"}
                                                </div>
                                            </div> */}
                                                <div className="col-4 px-1">
                                                    <div
                                                        className={
                                                            status === "inbound"
                                                                ? JSON.stringify(inboundCallRef.current) !== "{}" &&
                                                                  Number(inboundCallRef.current?.participant_caller_status || 0) === 1 &&
                                                                  //   Number(inboundCallRef.current?.participant_agent_status || 0) === 1 &&
                                                                  Number(inboundCallRef.current?.participant_client_status || 0) === 1
                                                                    ? "call-action-btn-active"
                                                                    : "call-action-btn-inactive"
                                                                : status === "outbound"
                                                                  ? JSON.stringify(outboundCall) !== "{}" &&
                                                                    Number(outboundCall?.participant_caller_status || 0) === 1 &&
                                                                    //   Number(outboundCall?.participant_agent_status || 0) === 1 &&
                                                                    Number(outboundCall?.participant_client_status || 0) === 1
                                                                      ? "call-action-btn-active"
                                                                      : "call-action-btn-inactive"
                                                                  : "call-action-btn-inactive"
                                                        }
                                                        onClick={() => {
                                                            if (!["inbound", "outbound"].includes(status)) return;
                                                            if (status === "inbound")
                                                                if (
                                                                    Number(inboundCallRef.current?.participant_caller_status || 0) === 0 ||
                                                                    // Number(inboundCallRef.current?.participant_agent_status || 0) === 0 ||
                                                                    Number(inboundCallRef.current?.participant_client_status || 0) === 0
                                                                )
                                                                    return;
                                                            if (status === "outbound")
                                                                if (
                                                                    Number(outboundCall?.participant_caller_status || 0) === 0 ||
                                                                    // Number(outboundCall?.participant_agent_status || 0) === 0 ||
                                                                    Number(outboundCall?.participant_client_status || 0) === 0
                                                                )
                                                                    return;

                                                            call_action("merge call v2");
                                                        }}>
                                                        {buttonActive.call_action["merge call v2"] ? <Loader type="circle" /> : "Merge calls"}
                                                    </div>
                                                </div>
                                                <div className="col-4 px-1">
                                                    <div
                                                        className={
                                                            status === "inbound"
                                                                ? JSON.stringify(inboundCallRef.current) !== "{}" &&
                                                                  Number(inboundCallRef.current?.participant_caller_hold || 0) === 1 &&
                                                                  Number(inboundCallRef.current?.participant_client_status) === 0
                                                                    ? "call-action-btn-active"
                                                                    : "call-action-btn-inactive"
                                                                : status === "outbound"
                                                                  ? JSON.stringify(outboundCall) !== "{}" && Number(outboundCall?.participant_caller_hold || 0) === 1
                                                                      ? "call-action-btn-active"
                                                                      : "call-action-btn-inactive"
                                                                  : "call-action-btn-inactive"
                                                        }
                                                        onClick={() => {
                                                            if (!["inbound", "outbound"].includes(status)) return;
                                                            if (status === "inbound" && Number(inboundCallRef.current?.participant_caller_hold || 0) === 0 && Number(inboundCallRef.current?.participant_client_status) === 0) return;
                                                            if (status === "outbound" && Number(outboundCall?.participant_caller_hold || 0) === 0) return;
                                                            call_action("hold v2", { participant: "caller", hold: 0 });
                                                        }}>
                                                        {buttonActive.call_action["hold"] ? <Loader type="circle" /> : "Return to caller"}
                                                    </div>
                                                </div>
                                                <div className="col-4 px-1">
                                                    <div
                                                        className={
                                                            status === "inbound"
                                                                ? JSON.stringify(inboundCallRef.current) !== "{}" && Number(inboundCallRef.current?.participant_client_status || 0) !== 0
                                                                    ? //   &&Number(inboundCallRef.current?.participant_agent_status || 0) === 1
                                                                      "call-action-btn-active"
                                                                    : "call-action-btn-inactive"
                                                                : status === "outbound"
                                                                  ? JSON.stringify(outboundCall) !== "{}" && Number(outboundCall?.participant_client_status || 0) !== 0
                                                                      ? // && Number(outboundCall?.participant_agent_status || 0) === 1
                                                                        "call-action-btn-active"
                                                                      : "call-action-btn-inactive"
                                                                  : "call-action-btn-inactive"
                                                        }
                                                        onClick={() => {
                                                            if (!["inbound", "outbound"].includes(status)) return;
                                                            if (status === "inbound") if (Number(inboundCallRef.current?.participant_client_status || 0) === 0) return;
                                                            // || Number(inboundCallRef.current?.participant_agent_status || 0) === 0) return;
                                                            if (status === "outbound") if (Number(outboundCall?.participant_client_status || 0) === 0) return;
                                                            // || Number(outboundCall?.participant_agent_status || 0) === 0) return;

                                                            call_action("drop participant v2", { participant: "client" });
                                                        }}>
                                                        {buttonActive.call_action["drop participant"] ? <Loader type="circle" /> : "Hang up client"}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div id="vca-right-phone-outbound" className={["ringing", "inbound", "wrapping up", "outbound"].includes(status) ? "d-none" : ""}>
                                            <div className="fw-400">Outbound</div>
                                            <div>
                                                <div className="row">
                                                    <div className="col-3 pr-0">
                                                        <Select
                                                            container={false}
                                                            options={(Number(user.user_id) === 1 ? ["+61", "+1", "+63", "+44", "+971"] : ["+61", "+1", "+63", "+44"]).map((a: any) => {
                                                                return { label: a, value: a };
                                                            })}
                                                            name="outboundprefix"
                                                            placeholder="prefix"
                                                            className="input-small w-100"
                                                            hideLabel
                                                            defaultValue={outboundCall.caller_prefix}
                                                            updateChange={(v) => {
                                                                setOutboundCall({ ...outboundCall, caller_prefix: v });
                                                                outboundCallRef.current = {
                                                                    ...outboundCall,
                                                                    caller_prefix: v,
                                                                };
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="col-9">
                                                        <Input
                                                            container={false}
                                                            className="input-small"
                                                            name="destination"
                                                            placeholder="Destination"
                                                            type="text"
                                                            defaultValue={outboundCall.destination}
                                                            changeDefault={new Date().getTime()}
                                                            updateChange={(v) => {
                                                                setOutboundCall({ ...outboundCall, destination: v });
                                                                outboundCallRef.current = {
                                                                    ...outboundCall,
                                                                    destination: v,
                                                                };
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                                <Select
                                                    options={(activeClient?.dids || []).map((a: any) => {
                                                        if (a.did.substring(0, 7) === "+611300") return;
                                                        return { label: a.did, value: a.did };
                                                    })}
                                                    name="outbounddid"
                                                    placeholder="DID"
                                                    className="input-small"
                                                    defaultValue={outboundCall.did || ""}
                                                    updateChange={(v) => {
                                                        setOutboundCall({ ...outboundCall, did: v });
                                                        outboundCallRef.current = {
                                                            ...outboundCall,
                                                            did: v,
                                                        };
                                                    }}
                                                />

                                                {(customerAlerts || []).length === 0 ? (
                                                    <></>
                                                ) : (
                                                    <>
                                                        {(customerAlerts || []).map((a: any, i: number) => {
                                                            if (a.alert?.alert === null || a.alert === null || Number(a.status) !== 1) return <React.Fragment key={i} />;
                                                            return (
                                                                <div key={i} className="light-red-bg p-2 small mb-1">
                                                                    <table>
                                                                        <tbody>
                                                                            <tr>
                                                                                <td className="vertical-align-top">
                                                                                    <b>Name</b>
                                                                                </td>
                                                                                <td>
                                                                                    {a.first_name} {a.lsst_name}
                                                                                </td>
                                                                            </tr>
                                                                            <tr>
                                                                                <td className="vertical-align-top">
                                                                                    <b>Alert</b>
                                                                                </td>
                                                                                <td dangerouslySetInnerHTML={{ __html: he.decode(a.alert.alert || "") }}></td>
                                                                            </tr>
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            );
                                                        })}
                                                    </>
                                                )}

                                                <Button
                                                    className="w-100"
                                                    hideContainer
                                                    small
                                                    active={buttonActive.check_customer_alerts}
                                                    disabled={(outboundCall.destination || "").length === 0}
                                                    value="Check customer alerts"
                                                    onClick={() => {
                                                        check_customer_alerts(outboundCall.caller_prefix + outboundCall.destination);
                                                    }}
                                                />
                                                <div className="row">
                                                    <div className="col-12">
                                                        <Button
                                                            className="w-100 mt-2"
                                                            color="blue"
                                                            hideContainer
                                                            small
                                                            value="Call (Non-billable)"
                                                            disabled={(outboundCall?.destination || "").length === 0 || (outboundCall?.did || "").length === 0 || buttonActive?.outbound}
                                                            onClick={async () => {
                                                                make_outbound("false");
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="col-12">
                                                        <Button
                                                            className="w-100 mt-2"
                                                            color="green"
                                                            hideContainer
                                                            small
                                                            value="Call (billable)"
                                                            disabled={(outboundCall?.destination || "").length === 0 || (outboundCall?.did || "").length === 0 || buttonActive?.outbound}
                                                            onClick={async () => {
                                                                make_outbound("true");
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div ref={(_) => vca_right_contact(_)} id="vca-right-contacts">
                                            <div className="fw-400">Contacts</div>
                                            <Input
                                                container={false}
                                                className="input-small"
                                                name="Searchcontacts"
                                                placeholder="Search contacts"
                                                type="text"
                                                updateChange={(v) => {
                                                    setSearchContacts(v);
                                                }}
                                            />
                                            {(() => {
                                                // Only filter if conditional forms are enabled
                                                const isConditionalFormEnabled = Number(activeClient?.conditional_form?.status || 0) === 1;

                                                let contactsToShow = activeClient?.contacts || [];

                                                // console.log("-".repeat(50));
                                                // console.log(Number(activeClient?.conditional_form?.status || 0));
                                                // console.log("/".repeat(50));
                                                // console.log(contactsToShow);
                                                // console.log("+".repeat(50));

                                                if (isConditionalFormEnabled) {
                                                    if (currentQuestionContacts.length === 0) {
                                                        // No contacts assigned to current question
                                                        return <div className="text-muted small mt-3">No contacts assigned to this question</div>;
                                                    }

                                                    // Filter contacts based on current question's contact UUIDs
                                                    // De-duplicate by uuid and maintain original order
                                                    const seenUuids = new Set<string>();
                                                    contactsToShow = contactsToShow.filter((contact: any) => {
                                                        if (currentQuestionContacts.includes(contact.uuid) && !seenUuids.has(contact.uuid)) {
                                                            seenUuids.add(contact.uuid);
                                                            return true;
                                                        }
                                                        return false;
                                                    });
                                                }

                                                // Render filtered contacts
                                                return contactsToShow.map((a: any, i: number) => {
                                                    if ((a?.first_name || "").toLowerCase().includes((searchContacts || "").toLowerCase()) || (a?.last_name || "").toLowerCase().includes((searchContacts || "").toLowerCase())) {
                                                        return (
                                                            <div className={`${Number(a.index) === activeTransfer ? "vca-right-contacts-container-active" : ""} vca-right-contacts-container`} key={i}>
                                                                <div className="fw-400">
                                                                    {a.first_name} {a.last_name}
                                                                </div>
                                                                <div>{a.job_title || "-"}</div>
                                                                <div>{a.short_description || "-"}</div>
                                                                <div className="row mt-2">
                                                                    <div className="col-6 pr-1">
                                                                        <Button
                                                                            className="w-100"
                                                                            value={a.email ? "Message" : "No Email"}
                                                                            hideContainer
                                                                            small
                                                                            disabled={!a.email}
                                                                            onClick={() => {
                                                                                if (a.email) {
                                                                                    setSendMessage({ ...sendMessage, to_name: a.first_name + " " + a.last_name, to_email: a.email, soucre: "email", to_sms: a.mobile });
                                                                                }
                                                                            }}
                                                                        />{" "}
                                                                    </div>
                                                                    <div className={a.preferred_phone ? "col-6 pl-1" : "d-none"}>
                                                                        <Button
                                                                            className="w-100"
                                                                            value="Transfer"
                                                                            active={buttonActive.call_action[status === "outbound" ? "make call outbound" + a.index : "make call v2" + a.index]}
                                                                            hideContainer
                                                                            small
                                                                            onClick={() => {
                                                                                setActiveTransfer(a.index);
                                                                                call_action(status === "outbound" ? "make call outbound" : "make call v2", { phone_number: a.preferred_phone, btn_index: a.index });
                                                                            }}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                });
                                            })()}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Home;
